import { ApplicationController, randomId, raw } from '@aha-app/mvc';
import type ClientBase from '../collaboration/ClientBase';
import Client from '../collaboration/Client';
import LoopbackClient from '../collaboration/LoopbackClient';
import CursorManager from '../collaboration/CursorManager';
import type CursorManagerBase from '../collaboration/CursorManagerBase';
import LoopbackCursorManager from '../collaboration/LoopbackCursorManager';
import type UndoManager from '../collaboration/UndoManager';
import DataStore from '../collaboration/DataStore';
import DataStoreGlobal from '../collaboration/DataStoreGlobal';
import hotkeys from 'hotkeys-js';
import tools from '../tools';
import type Tool from '../tools/Tool';
import DefaultsManager, {
  noteColorPalette,
  getCurrentColorPalette,
} from '../utils/DefaultsManager';
import {
  zoomAndPanTo,
  getTargetViewport,
  animateViewportTransform,
  createViewportHash,
  type ZoomToOptions,
  type TransformMatrix,
} from '../utils/zoom';
import Serialization, {
  type DocData,
  type TableKey,
} from '../collaboration/Serialization';
import Shape from '../objects/Shape';
import trackEvent from '../utils/analytics';

import type Stylus from '../stylus/Stylus';
import StylusPointer from '../stylus/StylusPointer';
import StylusPan from '../stylus/StylusPan';
import StylusShape from '../stylus/StylusShape';
import StylusCurrentShape from '../stylus/StylusCurrentShape';
import StylusCurrentFrame from '../stylus/StylusCurrentFrame';
import StylusCurrentPen from '../stylus/StylusCurrentPen';
import StylusNote from '../stylus/StylusNote';
import StylusConnector from '../stylus/StylusConnector';
import StylusPen from '../stylus/StylusPen';
import StylusHighlighter from '../stylus/StylusHighlighter';
import StylusFrame from '../stylus/StylusFrame';
import {
  StylusFrameCustom,
  StylusFrameWide,
  StylusFrameSquare,
} from '../stylus/StylusFrameRatio';
import StylusFrameAutolayout from '../stylus/StylusFrameAutolayout';
import StylusTable from '../stylus/StylusTable';
import StylusSmartObject from '../stylus/StylusSmartObject';
import StylusSvgStamp, {
  type StylusSvgStampName,
} from '../stylus/StylusSvgStamp';
import StylusTemplate from '../stylus/StylusTemplate';
import StylusWireframe from '../stylus/StylusWireframe';
import StylusAssistant from '../stylus/StylusAssistant';
import StylusAssistantPromo from '../stylus/StylusAssistantPromo';
import StylusWireframePromo from '../stylus/StylusWireframePromo';
import StylusComponent from '../stylus/StylusComponent';
import type StylusCustomShape from '../stylus/StylusCustomShape';
import * as Sentry from '@sentry/browser';

import type Fuzzer from '../../test/Fuzzer';
import type Connector from '../objects/Connector';
import Crop from '../objects/cropping/Crop';
import Debug from 'debug';
import type React from 'react';
import type Canvas from '../objects/Canvas';
import {
  type RecordId,
  type CreatableRecordType,
  type UserInfo,
  type CursorPosition,
  type CreatedRecordMetadata,
  type Rect,
  type RecordLinkItem,
  type DistributeUnion,
  type RecordLinkManager,
  type RelatedRecordManager,
} from '../types';
import type WidgetsController from './WidgetsController';
import StylusComment from '../stylus/StylusComment';
import Comment from '../objects/Comment';
import AnnotationsManager from '../utils/AnnotationsManager';
import { ALLOWED_IMAGE_TYPES } from '../shapes/ShapeImage';
import type ShapeImage from '../shapes/ShapeImage';
import DependencyLineManager from '../utils/DependencyLineManager';
import {
  isComment,
  isSimpleGroup,
  isSimpleSelection,
  hasStyleCopyPasteBehaviors,
  isTable,
  isShapeImage,
  isFrameLike,
  isShapeSpriteIcon,
  isFrame,
  hasLinkToUrlBehaviors,
  hasFrameMemberBehaviors,
  hasEmbeddedTextBehaviors,
  isMindMap,
  isMindMapLeaf,
} from '../objects/guards';
import type ShapeVideo from '../shapes/ShapeVideo';
import { ALLOWED_VIDEO_TYPES } from '../shapes/ShapeVideo';
import { type IPoint, type IEvent } from 'fabric/fabric-impl';
import { type ContextMenuType } from '../tools/ToolMenu';
import type {
  ISidePanelController,
  SidePanelExternalState,
  WhiteboardRecordInfo,
  WhiteboardRecordTypeNames,
  WhiteboardRecordModels,
  RecordInterface,
  IRecordManager,
  CreateRecord,
  RecordReferenceStruct,
} from '../shared-types';
import {
  type StyleDefinition,
  type StyleCopyPastable,
} from '../objects/StyleCopyPasteBehaviors';
import { fabric } from 'fabric';
import StylusDevice from '../stylus/StylusDevice';
import StylusMindMap from '../stylus/StylusMindMap';
import { applySmartZonesAtPoint } from '../services/smartZoneService';
import HoverController, { type HoveredLink } from './HoverController';
import type PresentationController from './PresentationController';
import { type Connection } from 'sharedb/lib/client';
import { extractWhiteboardId } from '../utils/urls';
import version from '../version';
import type IntegrationsController from './IntegrationsController';
import HistoryManager from './HistoryManager';
import type { WhiteboardMessage } from '../components/messages/WhiteboardMessageComponent';
import { type ShapeName } from '../shapes';
import ToolPaste, { parseUrl } from '../tools/ToolPaste';
import { type IDataStoreRecordBehaviors } from '../collaboration/DataStoreRecordBehaviors';
import CardLayoutManager from '../CardLayoutManager';
import { normalizeRecordType } from '../utils/record';
import { CONNECTOR_END_STYLE } from '../constants';
import { recordTypeHasCardLayout } from '../cardLayouts';
import { type StylusDefinition } from '../stylus/definitions';
import type CollaborationBehaviors from '../objects/CollaborationBehaviors';
import ShapeSmartRecord from '../shapes/ShapeSmartRecord';
import { debounce } from 'lodash';
import { ADVANCED_SHAPES } from '../utils/shapes';
import RecordingRenderManager from '../utils/RecordingRenderManager';
import { SpriteSvg } from '../toolbars/ObjectMenu/SpriteSvg';

// @ts-expect-error see app/assets. This is also mocked in serve.js
import { loadCloudArchIcons } from 'javascripts/loadCloudArchIcons';
import { ConnectorRouting } from '../objects/ConnectorRouting';
import {
  createDataStoreCustomShapes,
  type DataStoreCustomShapes,
} from '../collaboration/DataStoreCustomShapes';
import {
  type CustomShapeNameModal,
  type CustomShapeConfirmDeleteModal,
} from '../stylus/CustomShapeModals';
import SimpleSelection from '../fabric/SimpleSelection';
import type StylusBlock from '../stylus/StylusBlock';
import { getBlockStyluses } from '../stylus/StylusBlock';
import ShapeDragManager from '../toolbars/ShapeDragManager';
import AssistantService, {
  getWhiteboardContent,
  type WhiteboardOperation,
} from '../services/assistant';
import SelectionCycleManager from '../utils/SelectionCycleManager';
import { StylusAddAhaView } from '../stylus/StylusAddAhaView';
import { smartObjectsEnabled } from '../objects/SmartObject';
import { StylusSmartZone } from '../stylus/StylusSmartZone';
import StylusNoteStack from '../stylus/StylusNoteStack';
import StylusLinkObjectSelection from '../stylus/StylusLinkObjectSelection';
import { type LinkToUrlBehaviorsInterface } from '../objects/LinkToUrlBehaviors';
import { AhaEditorUtils } from '@aha-app/aha-editor-v2';
import {
  EMBEDDED_CONTENT_HEIGHT,
  EMBEDDED_CONTENT_WIDTH,
} from '../objects/EmbeddedContentBehaviors';
import { type RoadmapImportInfo } from '../components/modals/RoadmapImportModalController';
import { type WhiteboardProps } from '../Whiteboard';
import { createSequentialShape } from '../services/duplication/duplicatingObjects';

const debug = Debug('whiteboard:ui');
const debugAssistant = Debug('whiteboard:assistant');

// Define the amount to pan per arrow key press
const PAN_INCREMENT = 10;

export const BASIC_SHAPES: ShapeName[] = [
  'roundRect',
  'rect',
  'circle',
  'triangle',
  'diamond',
  'octagon',
  'pentagon',
  'parallelogram',
  'trapezoid',
  'processEnd',
  'openBracket',
  'closedBracket',
  'callOutBox',
  'certificate',
  'star',
  'cylinder',
  'cloud',
  'arrow',
  'circleArrow',
  'manualInput',
  'offpageConnector',
  'preparation',
  'subroutine',
  'document',
];

type ComponentType = 'stack' | 'swimlane' | 'board';

type ObjectType = 'table' | 'smartObject' | ComponentType;

type PointerInputType = 'auto' | 'mouse' | 'trackpad';

export interface PlaceObjectsOptions extends ZoomToOptions {
  select?: boolean;
  placement?: 'right' | 'bottom';
  shouldZoom?: boolean;
}

export const MODE = {
  DRAW: 'MODE_DRAW',
  CROP: 'MODE_CROP',
};

interface ContextMenu extends IPoint {
  type: ContextMenuType;
  canvasX?: number;
  canvasY?: number;
}

export interface MenuInfo {
  x: number;
  y: number;
  object: Connector;
  pointIndex: number;
}

interface CreateRecordsState {
  recordType: CreatableRecordType;
  data: CreateRecord[];
}

type UploadAttachment = (data: { file: unknown }) => Promise<string>;
type DataURL = `data:${string}`;

const defaultWhiteboardControllerState = () => ({
  // Whether the whiteboard is currently connecting. It may be temporarily
  // readOnly while connecting to the collaboration server.
  isConnecting: false,
  // Whether the canvas is ready for use.
  canvasReady: false,
  // True when canvas has no objects
  isEmpty: null,
  // True after the initial subscribe completes
  isLoaded: false,
  // Whether the object toolbar is currently displayed.
  objectToolbarVisible: false,
  // Current stylus - what type of drawing we are about to do. Null == pointer.
  currentStylus: null,
  // Stylus instance the menu is currently visible for. Null == no menu.
  currentStylusMenu: null,
  // Keep selected stylus after completing actions if true
  stylusLocked: false,
  // {top,left,width,height} in window coordinates of the current selection.
  selectedRect: null,
  // Location of context menu {x,y} when open.
  contextMenu: null,
  // Information about connector point menu {x, y, object, pointIndex} when open.
  connectorPointMenu: null as MenuInfo | null,
  // connector shape menu visibility
  connectorShapeMenu: false,
  // Whether the canvas is currently being grabbed
  isPanning: false,
  // Indicates keydown/up for space bar panning, similar to right click pan flag
  spaceBarPan: false,

  // Collaboration cursors, indexed by cursorId.
  cursors: {},

  // userId -> userName hash, used to display object author names
  userInfo: {},

  // Whether the template modal is visible.
  choosingTemplate: false,

  // Create records modal information. The modal is visible when filled in
  createRecords: false as false | CreateRecordsState,

  showAddAhaViewModal: false,
  infoForRoadmapImportModal: undefined,

  customShapeNameModalProps: null as
    | Parameters<typeof CustomShapeNameModal>[0]
    | null,

  customShapeConfirmDeleteModalProps: null as
    | Parameters<typeof CustomShapeConfirmDeleteModal>[0]
    | null,

  // Track changes to pan or zoom to force a redraw of the cursor layer.
  panZoomState: 0,

  // An object is being transformed
  hasActiveObjectTransformation: false,

  // Message to show to user.
  message: null,

  // External content interaction notification
  externalContentInteractive: false,

  // Keep track of active attachment uploads
  activeAttachmentUploads: [],

  // Current whitebaord mode, can be one of the following
  // DRAW: Regular canvas operation mode
  // CROP: There is an active crop action, interaction on the canvas other
  //       than the crop itself will reset the mode to DRAW.
  mode: MODE.DRAW,

  // Image that is currently being cropped, should only have a value when mode
  // is set to CROP.
  imageToCrop: null,

  // Object that handles crop interactions for images.
  crop: Crop,

  // Display shape authors on supported shapes
  showShapeAuthors: false,

  // Display the dynamic background grid
  showBackgroundGrid: false,

  // Pointer input device preference: auto-detect, force mouse, or force trackpad
  pointerInputType: 'auto' as PointerInputType,

  debug: {
    // Show a debugging scale.
    enableScale: false, // eslint-disable-line no-undef
    // Show a debugging grid.
    enableGrid: false, // eslint-disable-line no-undef
    // Show debug output from the orthogonal router.
    enableOrthogonalRouterDebug: false,
    // Show debug stuff around mindmaps
    mindmap: {
      // Show colour on MindMapBranch
      showBranches: false,
      // Allow selection of MindMapBranch and MindMapConnector
      selectable: false,
      // Play animations at 10% speed
      slowAnimations: false,
    },
  },

  // ID of current client user
  userId: undefined,

  // Used for testing to ensure controller finishes initializing
  ready: false,

  // Undo/redo availability
  hasUndo: false,
  hasRedo: false,

  // Used for rendering drawers
  drawerPath: '',

  draftingComment: false,
  sidePanelOpen: false,
  sidePanelActiveItemId: undefined as string | undefined,
  showResolvedComments: false,

  // The object that opened link to selection modal
  urlInputModalObject: null,

  annotationIdCache: new Set(),
  styleClipboardContents: null as StyleDefinition | null,

  // Set this to force an inline re-rendering of the toolbar
  // is used in the key of the toolbar so incrementing it will force a re-render
  toolbarReRenderCount: 0,

  objectMenuPinned: false,

  aiAssistantVisible: true,

  // Whether the pointer input help modal is visible
  showPointerInputHelpModal: false,
});

interface IWhiteboardControllerState {
  error?: string;
  contextMenu?: ContextMenu;
  selectedRect?: Rect | null;
  sidePanelActiveItemId?: string;
  userId: RecordId | null;
  userInfo: Record<
    RecordId,
    { value?: UserInfo; resolver?: Promise<UserInfo> }
  >;
  isEmpty?: boolean;
  message?: WhiteboardMessage;
  currentStylus: Stylus;
  activeAttachmentUploads: RecordId[];
  cursors: Record<number, CursorPosition>;
  exportFileName?: string;
  searchBarVisible?: boolean;
  urlInputModalObject?: fabric.Object & LinkToUrlBehaviorsInterface;
  aiAssistantVisible: boolean;
  drawerOpenedByEvent?: MouseEvent;
  infoForRoadmapImportModal?: RoadmapImportInfo;
}

// infer what we can from the initial state object above, with overrides as necessary
type WhiteboardControllerState = Omit<
  ReturnType<typeof defaultWhiteboardControllerState>,
  keyof IWhiteboardControllerState
> &
  IWhiteboardControllerState;

interface WhiteboardAccess {
  readOnly: boolean;
  allowUpdate: boolean;
  allowAnnotate: boolean;
}

interface WhiteboardController
  extends ApplicationController<WhiteboardControllerState, WhiteboardProps> {}

class WhiteboardController extends ApplicationController<
  WhiteboardControllerState,
  WhiteboardProps
> {
  static initialState = defaultWhiteboardControllerState();

  client: ClientBase;
  cursorManager: CursorManagerBase;
  undoManager: UndoManager;
  widgetsController: WidgetsController;
  defaultsManager: DefaultsManager;
  fuzzer?: Fuzzer; // used in demo whiteboard only

  stylusCurrentShape: StylusCurrentShape;
  currentConnectorStylus: StylusConnector;
  stylusPointer: StylusPointer;
  stylusPan: StylusPan;
  stylusRect: StylusShape;
  stylusText: StylusShape;
  stylusNote: StylusNote;
  currentStampStylus: StylusSvgStamp;
  stylusPen: StylusPen;
  stylusImage: StylusShape;
  stylusHighlighter: StylusHighlighter;
  StylusFrame: StylusFrame;
  StylusFrameAutolayout: StylusFrameAutolayout;
  StylusTemplate: StylusTemplate;
  stylusAssistant: StylusAssistant;
  stylusAssistantPromo: StylusAssistantPromo;
  stylusTable: StylusTable;
  stylusComment: StylusComment;
  stylusIcon: StylusShape;
  stylusSpriteIcon: StylusShape;
  stylusWireframe: StylusWireframe;
  stylusWireframePromo: StylusWireframePromo;
  paletteStyluses: Array<{ stylus: Stylus; tool?: typeof Tool }>;
  connectorShapeMenuShapes: ShapeName[];
  allWireframes: string[];
  devices: string[];
  connectorStyluses: StylusConnector[];
  stampStyluses: Array<Array<StylusSvgStamp>>;
  switchableShapes: ShapeName[];
  shapeStyluses: StylusShape[];
  connectorShapeMenuStyluses: StylusShape[];
  advancedStyluses: Array<
    StylusAddAhaView | StylusShape | StylusTable | StylusMindMap
  >;

  componentStyluses: StylusComponent[];
  customShapesStyluses: StylusCustomShape[] = [];
  smartRecordStyluses: Array<StylusShape>;
  penStyluses: Stylus[];
  noteStyluses: StylusNote[];
  noteStackStylus: StylusNoteStack;
  cleanupSingleClickTextEditing?: () => void;
  wireframeStyluses: StylusWireframe[];
  blockStyluses: StylusBlock[];
  deviceStyluses: StylusDevice[];
  frameRatioStyluses: [
    StylusFrameCustom,
    StylusSmartZone,
    StylusFrameWide,
    StylusFrameSquare
  ];

  sendShapeCreationAnalytics = true;

  initialVersion: number;

  hasLocalStorageSupport: boolean = false;

  sidePanelController: ISidePanelController | null;

  // If a connection takes longer than this (in ms) then show connecting message
  // to user.
  static CONNECT_MESSAGE_TIMEOUT = 1000;

  // period that we allow a connection to drop before making the whiteboard readonly
  static FLAP_TIMEOUT = 30000;

  // Non-MVC mirror of state.currentStylus
  _currentStylus?: Stylus;

  _stylusBeforePan?: Stylus;

  // Last used shape stylus.
  // TODO: This should be in `state`, but putting the stylus in state pollutes the whole fabric object hierarchy.
  currentShapeStylus: StylusCurrentShape;
  defaultShapeStylus: StylusShape;

  // Last used frame stylus.
  currentFrameStylus: StylusCurrentFrame;
  defaultFrameStylus: StylusFrame;

  // Last used pen stylus.
  currentPenStylus: StylusCurrentPen;
  defaultPenStylus: StylusPen;

  // Fabric canvas instance does not go in state - we don't want to
  // watch it for changes.
  canvas: Canvas;

  // Data stores for serialized objects.
  dataStoreObjects: DataStore<fabric.Object & IDataStoreRecordBehaviors>;
  dataStoreGlobal: DataStoreGlobal | null = null;
  dataStoreCustomShapes: DataStoreCustomShapes;
  // Index of data stores by table key.
  dataStores: { [tableKey in TableKey]?: DataStore } = {};

  // DOM element that is the container for the whole whiteboard component.
  whiteboardContainer: HTMLDivElement | null = null;

  /** DOM element that is the container for the text editor.  */
  editorContainer: HTMLDivElement | null = null;
  /** DOM element for editing canvasx text */
  canvasxInput: HTMLInputElement | null = null;

  /** <input> element that will be used for file uploads. */
  imageInput = null as HTMLInputElement | null;

  /** Current clipboard contents. */
  clipboard = null;

  // Current position of the mouse.
  mousePosition: IPoint = { x: 0, y: 0 };

  /** Function to call to cancel the current zoom animation. */
  animationCancelFunction: (() => void) | null = null;

  /** Function to resolve an attachment ID to information about that attachment */
  resolveAttachmentInfo: (attachmentId: RecordId) => Promise<{ url: string }>;

  /** Function to duplicate attachments when they are pasted into a new whiteboard - returns the duplicate's ID */
  duplicateAttachments?: (
    attachmentMapping: { [objId: RecordId]: RecordId },
    whiteboardId: RecordId
  ) => Promise<{ [objId: RecordId]: RecordId }> = undefined;

  whiteboardId: RecordId | undefined;

  annotationsManager: AnnotationsManager;

  dependencyLineManager: DependencyLineManager;
  dependencyLineUpdateTimer: ReturnType<typeof setTimeout> | null = null;

  externalSetCommentsVisible?: (visible: boolean) => void;

  imageToCrop: Shape<ShapeImage> | null;
  crop: Crop | null;
  connection: Connection | undefined;

  uploadAttachment?: UploadAttachment;

  showedConnectingMessage?: boolean;
  connectionTimer?: NodeJS.Timeout;
  messageTimer?: NodeJS.Timeout;
  _temporaryHideObjectToolbarID?: ReturnType<Window['requestAnimationFrame']>;
  stylusStamp: StylusSvgStamp;
  objects: ObjectType[];

  // these are not actually set until startController is called, but we assume they
  // won't be accessed before then
  presentationController: PresentationController;
  integrationsController: IntegrationsController;
  shapeDragManager: ShapeDragManager;
  cardLayoutManager: CardLayoutManager;
  historyManager?: HistoryManager;

  /** External manager that handles creating records from shapes/smart record shapes */
  recordManager?: IRecordManager;
  /** External manager that helps with creating record links within the whiteboard */
  recordLinkManager?: RecordLinkManager;
  /** External manager that helps with finding related records for a record */
  relatedRecordManager?: RelatedRecordManager;
  /** Limited interface to the recordLinkController to allow the whiteboard controller
    to pass data from tools to the RecordLinkController */
  openRecordLinkModal: (items: RecordLinkItem[]) => void;

  /** Encapsulates special rendering behaviors.  */
  renderManager: RecordingRenderManager;

  /** Encapsulates ordering logic when tabbing through shapes on a whiteboard */
  selectionCycleManager: SelectionCycleManager;

  aiAssistantVisible: boolean;
  assistant: AssistantService;

  private readonly hotkeysToolScope: string;

  constructor() {
    super();
    this.hotkeysToolScope = `whiteboardTools-${randomId()}`;
  }

  async initialize() {
    this.actionShowMessage('Loading', true, true);
  }

  // Event system for passing events to/from canvas object and sidepanels and
  // other disconnected components.
  eventTarget: EventTarget = new EventTarget();

  startController(
    presentationController: PresentationController,
    integrationsController: IntegrationsController
  ) {
    this.presentationController = presentationController;
    this.integrationsController = integrationsController;
    this.cardLayoutManager = new CardLayoutManager(this);
    this.shapeDragManager = new ShapeDragManager(this);
    this.recordManager = this.props.recordManager;
    this.recordLinkManager = this.props.recordLinkManager;
    this.relatedRecordManager = this.props.relatedRecordManager;
    this.renderManager = new RecordingRenderManager();

    // Initialize history controller if historyDataManager is provided
    if (this.props.historyDataManager && this.props.whiteboardId) {
      this.historyManager = new HistoryManager(
        this,
        this.props.whiteboardId,
        this.props.historyDataManager
      );
    }

    this.moreMenuActionsState = this.moreMenuActionsState.bind(this);
    this.props.registerMenu?.(this.moreMenuActionsState);

    const {
      allowUpdate,
      allowAnnotate,
      fayeClient,
      connection,
      documentCollection,
      documentId,
      accountId,
      whiteboardId,
      userId,
      uploadAttachment,
      initialVersion,
      initialData,
      isFormField = false,
      duplicateAttachments,
      commentsSidebarId,
      externalSetCommentsVisible,
    } = this.props;

    this.state.userId = userId ?? null;
    this.whiteboardId = whiteboardId;
    this.state.aiAssistantVisible = false;

    this.annotationsManager = new AnnotationsManager({
      whiteboardId,
      commentsSidebarId,
      controller: this,
    });
    this.dependencyLineManager = new DependencyLineManager(this);
    this.externalSetCommentsVisible = externalSetCommentsVisible;
    this.selectionCycleManager = new SelectionCycleManager(this);

    this.connection = connection;

    this.createStyluses();
    this.defaultsManager = new DefaultsManager();

    // If we are read-only, and have data supplied, don't connect to
    // collaboration server. This means that we don't need any authentication
    // information - so the whiteboard viewer can be used by unauthenticated
    // users.
    const changesAllowed = this.props.allowUpdate || this.props.allowAnnotate;
    if ((!changesAllowed || isFormField) && initialData) {
      this.client = new LoopbackClient(this);
      this.cursorManager = new LoopbackCursorManager();
      this.actionClearMessage();
    } else {
      if (!connection) {
        throw new Error('No connection provided in collaborative mode');
      }
      if (!documentId) {
        throw new Error('No documentId provided in collaborative mode');
      }
      if (!accountId) {
        throw new Error('No accountId provided in collaborative mode');
      }
      if (!accountId) {
        throw new Error('No accountId provided in collaborative mode');
      }
      if (!documentCollection) {
        throw new Error('No documentCollection provided in collaborative mode');
      }

      if (!allowUpdate && !allowAnnotate) {
        // Don't show / send cursor positions for viewers
        this.cursorManager = new LoopbackCursorManager();
      } else {
        this.cursorManager = new CursorManager({
          fayeClient,
          documentPath: `${accountId}--whiteboard/${whiteboardId}`,
          resolveUserInfo: async userId =>
            await this.actionResolveUserInfo(userId),
          userId,
          cursors: this.state.cursors,
        });
      }
      this.client = new Client(
        this,
        connection,
        documentCollection,
        documentId
      );

      connection.on('state', newState => {
        switch (newState) {
          case 'connected':
            console.log('Connected to server');
            this.actionConnected();
            break;

          case 'connecting':
            this.actionConnecting();
            break;

          default:
            console.log('Disconnected from server');
            this.actionDisconnected();
            break;
        }
      });

      window.addEventListener('online', () => {
        console.log('Connection online');
        this.actionConnected();
      });
      window.addEventListener('offline', () => {
        console.log('Connection offline');
        this.actionDisconnected();
      });
      window.addEventListener(
        'hashchange',
        () => {
          this.actionNavigate();
        },
        false
      );
    }
    this.dataStoreObjects = new DataStore('objects', this.client);
    this.dataStoreGlobal = new DataStoreGlobal('global', this.client);
    this.dataStoreCustomShapes = createDataStoreCustomShapes(this);
    this.dataStores.global = this.dataStoreGlobal;
    this.dataStores.objects = this.dataStoreObjects;
    this.dataStores.customShapes = this.dataStoreCustomShapes;

    this.undoManager = this.client.undoManager;
    this.uploadAttachment = uploadAttachment;
    this.initialVersion = initialVersion ?? 1;
    this.duplicateAttachments = duplicateAttachments;

    this.maybeEndCropping = this.maybeEndCropping.bind(this);

    this.initClipboardLocalStorage();
    this.readStyleClipboardFromLocalStorage();
    this.readPointerInputTypeFromLocalStorage();

    void this.actionGetFileNameFromRecord();

    void this.loadSpriteSheets();

    this.assistant = new AssistantService(this);
  }

  changeProps(props: WhiteboardProps, oldProps: WhiteboardProps) {
    const { commentsVisible, commentsEnabled } = props;

    if (commentsEnabled && commentsVisible !== oldProps.commentsVisible) {
      this.actionUpdateCommentVisibility();
    }

    this.actionUpdateAccess();
  }

  // Handle cleanup.
  destroy() {
    hotkeys.deleteScope(this.hotkeysToolScope);
    this.cursorManager.destroy();
    this.canvas.deleted();
    this.client.destroy();
    this.annotationsManager?.destroy();
    window.removeEventListener('storage', this.handleLocalStorageEvent);
    this.cleanupSingleClickTextEditing?.();
  }

  documentDidLoad() {
    // Mark each data store as loaded
    Object.values(this.dataStores).forEach(dataStore => dataStore.loaded());

    this.state.isLoaded = true;

    this.actionNavigateOrFit();
  }

  public actionNavigateOrFit({
    animate = false,
    zoomInOnly = false,
    disableVptPersistence = false,
  } = {}) {
    // navigate to the linked or saved location, or zoom to fit if that didn't work
    const navigated = this.actionNavigate(
      undefined,
      animate,
      disableVptPersistence
    );

    if (!navigated) {
      this.actionZoomIntoView({ animate, zoomInOnly });
    }

    const hash = decodeURIComponent(window.location.hash.slice(1));
    if (hash.startsWith('sidepanel-')) {
      this.actionOpenSidePanel(hash.slice('sidepanel-'.length));
    }
  }

  handleLocalStorageEvent = (event: StorageEvent) => {
    const { key, storageArea } = event;
    if (
      !(storageArea === localStorage && key === '_whiteboard_style_clipboard')
    ) {
      return;
    }
    this.readStyleClipboardFromLocalStorage();
  };

  initClipboardLocalStorage() {
    try {
      localStorage.setItem('___check', '');
      localStorage.removeItem('___check');
      this.hasLocalStorageSupport = true;
    } catch {
      // No local storage, just ignore it?
    }
    if (!this.hasLocalStorageSupport) {
      return;
    }

    window.addEventListener('storage', this.handleLocalStorageEvent, false);
  }

  readPointerInputTypeFromLocalStorage() {
    if (!this.hasLocalStorageSupport) {
      return;
    }
    const value = localStorage.getItem('_whiteboard_pointer_input_type');
    if (value === 'auto' || value === 'mouse' || value === 'trackpad') {
      this.state.pointerInputType = value;
    }
  }

  readStyleClipboardFromLocalStorage() {
    if (!this.hasLocalStorageSupport) {
      return;
    }
    const value = localStorage.getItem('_whiteboard_style_clipboard');
    try {
      const parsed =
        value === null ? null : (JSON.parse(value) as StyleDefinition);
      this.state.styleClipboardContents = parsed;
      return parsed;
    } catch (e) {
      if (e instanceof SyntaxError) {
        // Bad JSON somehow
        Sentry.captureException(e);
      }
      throw e;
    }
  }

  actionCopyStyle(source: StyleCopyPastable) {
    if (!hasStyleCopyPasteBehaviors(source)) {
      return;
    }

    const clipboard = source.getCopyStyles();
    if (this.hasLocalStorageSupport) {
      localStorage.setItem(
        '_whiteboard_style_clipboard',
        JSON.stringify(clipboard)
      );
    }
    this.state.styleClipboardContents = clipboard;
  }

  actionPasteStyle() {
    const styles = this.state.styleClipboardContents;
    if (!styles) {
      return;
    }

    const targets = this.selection
      .getObjects()
      .filter(hasStyleCopyPasteBehaviors);

    for (const o of targets) {
      o.applyPaste(styles);
    }
  }

  dataStoreForKey(tableKey: string) {
    const dataStore = this.dataStores[tableKey];
    if (!dataStore) {
      throw new Error(`Unknown tableKey ${tableKey}`);
    }
    return dataStore;
  }

  // Helper to access current selection
  get selection() {
    return this.canvas.getActiveObject();
  }

  // Helper to access current detected device type
  get currentDetectedDeviceType(): 'mouse' | 'trackpad' {
    return this.canvas?.getCurrentDetectedDeviceType() ?? 'mouse';
  }

  // Object to show toolbar for.
  toolbarObject() {
    return this.canvas.getActiveObject();
  }

  // Based on the current selection return the list of tools that can be used
  // to operate on the objects.
  visibleObjectTools() {
    if (!this.props.allowUpdate || this.isReadOnly()) return null;
    if (this.isCropping()) return null;

    const obj = this.toolbarObject();
    if (!obj) return [];

    if (!obj.tools) {
      return [];
    }
    return obj.tools();
  }

  get canFacilitate() {
    return !this.props.readOnly && (this.props.canFacilitate ?? false);
  }

  get canPresent() {
    return this.props.canPresent ?? true;
  }

  get canVote() {
    return !this.props.readOnly && this.props.allowAnnotate;
  }

  get canDisplayTimer() {
    return !this.props.readOnly;
  }

  get canUpdateContent() {
    return !this.props.readOnly && this.props.allowUpdate;
  }

  async actionResolveUserInfo(userId: RecordId): Promise<UserInfo> {
    const { resolver, value } = this.state.userInfo[userId] || {};

    if (value) {
      return value;
    } else if (resolver) {
      return await resolver;
    } else if (this.props.resolveUserInfo) {
      const resolver = this.props.resolveUserInfo(userId).then(value => {
        this.state.userInfo[userId].value = value;
        this.canvas?.requestRenderAll();
        return value;
      });
      this.state.userInfo[userId] = { resolver };
      return await resolver;
    } else {
      return { displayName: '' };
    }
  }

  getResolvedUser(userId: RecordId) {
    return this.state.userInfo?.[userId]?.value;
  }

  actionCanvasMouseUp = (event?: any) => {
    this.closeAhaDrawer(event?.e);
  };

  openRecordInDrawer(recordRef: RecordReferenceStruct, e?: MouseEvent) {
    if (typeof this.props.openRecordInDrawer === 'function') {
      this.props.openRecordInDrawer({ recordRef });
    }

    this.state.drawerOpenedByEvent = e;
  }

  closeAhaDrawer(eventTriggeringClose?: MouseEvent) {
    if (
      eventTriggeringClose &&
      eventTriggeringClose === this.state.drawerOpenedByEvent
    ) {
      debug('Drawer closed by event that opened it. Ignoring the close call.');
      return;
    }

    if ('actionCloseAhaDrawer' in this.proxiedThis) {
      this.proxiedThis.actionCloseAhaDrawer();
    }
  }

  handleDrop = (event: IEvent<DragEvent>) => {
    if (event?.e?.dataTransfer?.items) {
      const uriItem = Array.from(event.e.dataTransfer.items).find(
        item => item.type === 'text/uri-list'
      );

      if (uriItem) {
        this.mousePosition =
          event.absolutePointer || this.canvas.getPointer(event.e);
        uriItem.getAsString(url => {
          void tools.ToolPaste.pasteUrl(this, url);
        });
      }
    }
  };

  actionSetCanvas(
    canvas: Canvas,
    editorContainer: HTMLDivElement,
    canvasxInput: HTMLInputElement,
    whiteboardContainer: HTMLDivElement
  ) {
    this.canvas = canvas;
    this.editorContainer = editorContainer;
    this.canvasxInput = canvasxInput;
    this.whiteboardContainer = whiteboardContainer;
    this.state.canvasReady = true;

    this.registerKeyBindings();
    this.actionEnableToolKeys();

    this.actionResetStylus();

    const data = this.props.initialData;
    this.client.initialize(this.initialVersion, data ? raw(data) : undefined);

    if (this.props.debug?.makeFuzzer) {
      // Enable fuzzer in debug builds.
      this.fuzzer = this.props.debug?.makeFuzzer(this.proxiedThis);
    }
    if (process.env.TEST) {
      this.state.ready = true;
    }

    this.canvas.on('mouse:up', this.actionCanvasMouseUp);
    this.canvas.on('drop', this.handleDrop);

    // Add event handler for smart zone detection
    this.canvas.on('object:moved', e => {
      if (e.target) {
        const mousePoint = this.canvas.getPointer(e.e, true);
        void applySmartZonesAtPoint(this, e.target, mousePoint);
      }
    });

    const handlePaste = () => {
      void tools.ToolPaste.action({ controller: this });
    };

    this.whiteboardContainer.addEventListener('focus', () => {
      document.addEventListener('paste', handlePaste);
      this.actionFocus();
    });

    this.whiteboardContainer.addEventListener('blur', () => {
      document.removeEventListener('paste', handlePaste);
    });

    const handlePanZoom = () => {
      this.state.panZoomState += 1;
      this.actionUpdateViewportHash();
    };
    this.canvas.on('pan', handlePanZoom);
    this.canvas.on('zoom', handlePanZoom);

    // Set up single-click text editing with direct event listener
    if (window.featureFlags?.AW_491_whiteboards_single_click_edit) {
      this.setupSingleClickTextEditing();
    }
  }

  private setupSingleClickTextEditing() {
    let isStartingTextEdit = false;

    const handleKeyEvent = async (event: KeyboardEvent) => {
      // Don't activate unless whiteboard is focused
      if (document.activeElement !== this.whiteboardContainer) {
        return;
      }

      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      if (event.key.length > 1 || event.key === ' ') {
        return;
      }

      const anyTextEditing = this.canvas
        .getObjects()
        .some(obj => obj.textEditing);
      if (anyTextEditing) {
        return;
      }

      const selection = this.canvas.getActiveObject();

      if (!selection || !hasEmbeddedTextBehaviors(selection)) {
        return;
      }

      // Don't auto-enter text edit mode for frames and tables
      if (isFrame(selection) || isTable(selection)) {
        return;
      }

      if (isSimpleSelection(selection) && selection.getObjects().length > 1) {
        return;
      }

      const textObject = selection;
      if (textObject.textEditing || isStartingTextEdit) {
        return;
      }

      if (!textObject.textEditing) {
        isStartingTextEdit = true;
        event.preventDefault();
        event.stopPropagation();

        const shouldPrevent = textObject.preventTextEditing?.();
        if (shouldPrevent) {
          isStartingTextEdit = false;
          return;
        }

        if (textObject.setSelectedTextField && !textObject.activeTextField) {
          const fakeEvent = {
            absolutePointer: new fabric.Point(0, 0),
          } as fabric.IEvent;
          textObject.setSelectedTextField(fakeEvent);
        }

        if (textObject.activeTextField) {
          await textObject.activeTextField.startTextEditing();
          setTimeout(() => {
            AhaEditorUtils.Selection.selectAll(textObject.editor);
            AhaEditorUtils.Transforms.insertCharacter(
              textObject.editor,
              event.key
            );
          }, 10);

          isStartingTextEdit = false;
          return;
        }

        isStartingTextEdit = false;
      }
    };

    document.addEventListener('keydown', handleKeyEvent, true);
    this.whiteboardContainer?.addEventListener('keydown', handleKeyEvent);
    window.addEventListener('keydown', handleKeyEvent);

    this.cleanupSingleClickTextEditing = () => {
      document.removeEventListener('keydown', handleKeyEvent, true);
      this.whiteboardContainer?.removeEventListener('keydown', handleKeyEvent);
      window.removeEventListener('keydown', handleKeyEvent);
    };
  }

  actionSetImageInput(input) {
    this.imageInput = input;
  }

  isReadOnly() {
    return (
      this.props.readOnly ||
      this.presentationController?.isPresenting() ||
      this.client?.needsCatchup() ||
      // if rendered in a form field, we don't need a connection,
      // otherwise we're readonly until it connects. on dropped connection, we only
      // make the whiteboard readonly after the disconnectionTimer has elapsed
      (!this.props.isFormField &&
        !this.disconnectionTimer &&
        (this.connection?.state !== 'connected' ||
          this.state.isConnecting ||
          !window.navigator.onLine))
    );
  }

  allowPanAndZoom() {
    return (
      !this.props.isEmbeddedPreview &&
      !this.presentationController?.isPresenting()
    );
  }

  actionPanByIncrement(dx: number, dy: number) {
    // Only allow incremental panning if we're allowed to pan at all
    if (!this.allowPanAndZoom()) {
      return;
    }

    this.canvas.relativePan({ x: dx, y: dy });
  }

  hasFrames() {
    return this.presentationController?.hasFrames();
  }

  private prevAccess: WhiteboardAccess | undefined = undefined;

  actionSetShowAddAhaViewModal(showModal: boolean) {
    this.state.showAddAhaViewModal = showModal;
  }

  actionSetShowRoadmapImportModal(bookmarkInfo: RoadmapImportInfo | undefined) {
    this.state.infoForRoadmapImportModal = bookmarkInfo;
  }

  actionUpdateAccess() {
    const readOnly = this.isReadOnly();
    const { allowAnnotate, allowUpdate } = this.props;

    // if any access-related state has changed, sync it to the canvas
    if (
      this.prevAccess &&
      this.prevAccess.readOnly === readOnly &&
      this.prevAccess.allowAnnotate === allowAnnotate &&
      this.prevAccess.allowUpdate === allowUpdate
    ) {
      return;
    }

    if (!this.canvas) {
      return;
    }

    this.canvas.setReadOnly(readOnly, !!allowUpdate, !!allowAnnotate);

    this.prevAccess = {
      readOnly,
      allowUpdate: !!allowUpdate,
      allowAnnotate: !!allowAnnotate,
    };

    // ensure write-capable styluses don't persist if the user should not be able to edit
    if (!allowUpdate) {
      this.actionSetStylus(this.stylusPointer);
    }

    // ensure the default layout is loaded in case the whiteboard started out readonly
    void this.cardLayoutManager.loadDefaultCardLayout();
  }

  actionConnecting() {
    // Prepare to show a message if we don't connect in a reasonable time.
    this.showedConnectingMessage = false;
    this.connectionTimer = setTimeout(() => {
      this.actionShowMessage('Connecting to server', true);
      this.showedConnectingMessage = true;
    }, WhiteboardController.CONNECT_MESSAGE_TIMEOUT);
    this.state.isConnecting = true;
    this.actionUpdateAccess();
  }

  actionConnected() {
    clearTimeout(this.connectionTimer);
    if (this.disconnectionTimer) {
      clearTimeout(this.disconnectionTimer);
      delete this.disconnectionTimer;
    }
    if (this.showedConnectingMessage) {
      this.showedConnectingMessage = false;
      this.actionShowMessage('Connected');
    } else {
      this.actionClearMessage();
    }
    this.state.isConnecting = false;
    this.actionUpdateAccess();
    this.actionSetIsEmpty();
  }

  private disconnectionTimer?: ReturnType<typeof setTimeout>;

  actionBackedUp() {
    // The fact that you are disconnected is more relevant than the queue
    // being backed up.
    if (!this.showedConnectingMessage) {
      this.actionShowMessage(
        'You are out of sync with the server. Attempting to resync.',
        true,
        true
      );
    }
    this.actionUpdateAccess();
  }

  actionDisconnected() {
    if (!this.disconnectionTimer) {
      this.disconnectionTimer = setTimeout(() => {
        this.actionShowMessage(
          'You are offline. Attempting to reconnect.',
          true,
          true
        );
        this.showedConnectingMessage = true;
        this.actionUpdateAccess();

        delete this.disconnectionTimer;
      }, WhiteboardController.FLAP_TIMEOUT);
    }
  }

  actionSetIsEmpty() {
    const objects = this.canvas?.getObjects();
    this.state.isEmpty = !objects || objects.length === 0;
  }

  actionShowMessage(
    message: WhiteboardMessage | string,
    persistent = false,
    delay = false,
    duration = 5000
  ) {
    if (typeof message === 'string') {
      message = { type: 'plain', message };
    }

    clearTimeout(this.messageTimer);

    if (delay) {
      this.messageTimer = setTimeout(
        () => this.actionShowMessage(message, persistent),
        2000
      );
      return;
    }

    if (!persistent) {
      this.messageTimer = setTimeout(() => this.actionClearMessage(), duration);
    }

    this.state.message = message;
  }

  actionClearMessage() {
    clearTimeout(this.messageTimer);
    this.state.message = undefined;
  }

  actionShowPasteUnsupportedMessage() {
    if (ToolPaste.displayedPasteWarning) {
      return;
    }

    ToolPaste.displayedPasteWarning = true;

    this.actionShowMessage(
      'Your browser does not support pasting from the clipboard. Consider using Chrome or Edge.'
    );
  }

  // TODO: rename analytics events if we add tracking for other external embeds
  actionShowExternalContentExitButton(shouldShow) {
    this.state.externalContentInteractive = shouldShow;
  }

  // Ensure the whiteboard is focused so it receives keyboard events.
  actionFocus(resetToolKeys = true) {
    // setTimeout to ensure that the event that is causing focus has finished
    // (otherwise the click that caused the whiteboard to open may steal focus
    // back).
    setTimeout(() => {
      if (document.activeElement !== this.whiteboardContainer) {
        this.whiteboardContainer?.focus();

        // ensure only the whiteboard receives keyboard events for things like paste (looking at you, Safari)
        window.getSelection()?.removeAllRanges();
      }

      if (resetToolKeys) this.actionEnableToolKeys();
    }, 10);
  }

  actionZoomIntoView({ animate = false, zoomInOnly = false } = {}) {
    zoomAndPanTo(this, this.canvas.usedPortion(), {
      maxZoom: 1,
      animate,
      zoomInOnly,
    });
  }

  actionSetViewport(viewport: TransformMatrix) {
    this.cancelViewportAnimation();
    this.canvas.setViewportTransform(viewport);
    this.canvas.fire('zoom');
  }

  isObjectToolbarVisible() {
    return this.state.objectToolbarVisible && this.toolbarObject();
  }

  actionShowObjectToolbar() {
    const obj = this.toolbarObject();
    if (!obj || obj.hideObjectToolbar) {
      this.state.objectToolbarVisible = false;
      return;
    }

    this.actionUpdateSelectedRect();
    // The toolbar is only visible if the selected object is also visible.
    this.state.objectToolbarVisible = obj.isOnScreen();
  }

  actionUpdateSelectedRect() {
    const obj = this.toolbarObject();
    if (obj) {
      const selectedRect = this.canvas.rectToWindowCoords(
        obj.getBoundingRect(true)
      );
      this.state.selectedRect = selectedRect;
    }
  }

  actionForceRerenderObjectToolbar() {
    if (this.state.objectToolbarVisible) {
      this.actionHideObjectToolbar();
      if (this._temporaryHideObjectToolbarID) {
        cancelAnimationFrame(this._temporaryHideObjectToolbarID);
      }
      this._temporaryHideObjectToolbarID = requestAnimationFrame(() =>
        this.actionShowObjectToolbar()
      );
    }
  }

  actionRerenderToolbar() {
    this.state.toolbarReRenderCount++;
  }

  actionHideObjectToolbar() {
    this.state.objectToolbarVisible = false;
    this.state.selectedRect = null;
  }

  actionShowContextMenu(
    type: ContextMenuType,
    x: number,
    y: number,
    canvasPointer?: IPoint
  ) {
    this.state.contextMenu = {
      type,
      x,
      y,
      canvasX: canvasPointer?.x,
      canvasY: canvasPointer?.y,
    };
  }

  actionHideContextMenu() {
    this.state.contextMenu = undefined;
  }

  actionShowConnectorPointMenu(menuInfo: MenuInfo) {
    this.state.connectorPointMenu = menuInfo;
  }

  actionHideConnectorPointMenu() {
    this.state.connectorPointMenu = null;
  }

  actionShowConnectorShapeMenu() {
    this.state.connectorShapeMenu = true;
    // Avoid showing both shape menu and toolbar, as they overlap each other
    this.actionHideObjectToolbar();
  }

  actionHideConnectorShapeMenu() {
    this.state.connectorShapeMenu = false;
  }

  actionSetClipboard(clipboard) {
    this.clipboard = clipboard;
  }

  actionApplyTool(tool, params = {}, shouldRefocus = true) {
    // Complete any in-progress animation from the previous tool.
    this.cancelViewportAnimation();

    const selection = this.toolbarObject();

    // Make sure tool is enabled before we invoke it.
    if (this.isToolEnabled({ tool })) {
      tool.action({
        controller: this.proxiedThis,
        selection,
        ...params,
      });
      // so that we don't rely on it in the future?
      this.undoManager.completeUndoGroup();
    } else if (tool.disabledMessage) {
      this.actionShowMessage(tool.disabledMessage);
    }

    // Refocus the whiteboard in case we used a menu to choose the tool and
    // lost focus. There are some cases where we don't want to refocus right away,
    // such as when the aha-editor is focused
    if (shouldRefocus) {
      this.actionFocus();
    }
  }

  actionSetShapeStylus(stylus) {
    if (!(stylus instanceof StylusShape) || !stylus.nonBrushShape()) {
      // Just save the stylus and then have currentShapeStylus call the controller
      // instead of it understanding what is going on.
      this.currentShapeStylus.setCurrentStylus(stylus);
    }

    this.actionSetStylus(stylus);
    this.actionFocus();
  }

  actionSetFrameStylus(stylus) {
    // Save the stylus and then have currentFrameStylus call the controller
    // instead of it understanding what is going on.
    this.currentFrameStylus.setCurrentStylus(stylus);

    this.actionSetStylus(stylus);
    this.actionFocus();
  }

  actionSetPenStylus(stylus) {
    this.currentPenStylus.setCurrentStylus(stylus);

    this.actionSetStylus(stylus);
    this.actionFocus();
  }

  actionSetStylus(stylus: Stylus) {
    debug(`actionSetStylus: ${stylus.description()}`);

    if (
      !(
        stylus instanceof StylusLinkObjectSelection ||
        stylus instanceof StylusPan
      )
    ) {
      this.state.urlInputModalObject = undefined;
    }

    this.state.currentStylus?.deactivate();

    this._currentStylus = stylus;
    this.state.currentStylus = stylus;

    if (stylus instanceof StylusConnector) {
      this.currentConnectorStylus.options = stylus.options;
    } else if (stylus instanceof StylusSvgStamp) {
      this.currentStampStylus.options = stylus.options;
    }

    this.actionHideContextMenu();
    this.actionHideObjectToolbar();

    this.canvas.setDrawingCursor(stylus.cursor());
    stylus.activate();
  }

  actionUnlockStylus() {
    this.state.stylusLocked = false;
  }

  actionLockStylus() {
    if (raw(this.state.currentStylus) !== raw(this.stylusPointer))
      this.state.stylusLocked = true;
  }

  // When setting a tool via hotkey, we do not have access to the underlying
  // stylus. This allows us to respect the stylus's alwaysLockWhenSelected field
  actionUpdateStylusLock() {
    this.state.currentStylus.alwaysLockWhenSelected()
      ? this.actionLockStylus()
      : this.actionUnlockStylus();
  }

  actionSetStylusMenu(stylus) {
    if (this.state.objectMenuPinned) {
      if (stylus?.subMenu()) {
        // Currently we only support pinning the object menu
        const objectName = stylus.shapeName ?? stylus.wireframeName;
        if (!stylus.subMenuIncludes?.(objectName)) {
          this.actionSetMenuPinned(stylus.subMenu() == null);
          this.state.currentStylusMenu = stylus;
        }
      }
    } else {
      this.state.currentStylusMenu = stylus;
    }
  }

  actionSetMenuPinned(pinned: boolean) {
    this.state.objectMenuPinned = pinned;
    this.actionResetStylus(); // Reset stylus so that after pinning menu a stylus is not selected.
    if (!pinned) {
      // Close the menu when unpinning.
      this.actionSetStylusMenu(null);
    }
  }

  actionResetStylus() {
    if (!this.state.stylusLocked) {
      this.actionSetStylus(this.stylusPointer);
    } else {
      // We reset the stylus so the stylus is activated again after a control is used
      this._currentStylus && this.actionSetStylus(this._currentStylus);
    }
  }

  get stylusIsPointer() {
    return (
      this.state.currentStylus.isCurrentStylus(this.stylusPointer) ||
      this.state.currentStylus == null
    );
  }

  actionCreateRecords(recordType: CreatableRecordType, data: CreateRecord[]) {
    trackEvent(
      'Feature-7094629539429452113',
      'User opens creates records modal',
      { recordType }
    );

    this.state.createRecords = { recordType, data };
    this.actionDisableToolKeys();
  }

  actionAbortCreateRecords() {
    this.state.createRecords = false;
    this.actionEnableToolKeys();
  }

  actionAssignCreateRecordsToObjects(
    recordType: WhiteboardRecordTypeNames,
    data: CreatedRecordMetadata[]
  ) {
    for (const element of data) {
      const { recordId, id } = element;
      const object = this.dataStoreObjects.findRecordById(id) as Shape;
      object.setRecord(recordId, recordType);
    }
    this.canvas.requestRenderAll();
  }

  async actionChooseTemplate() {
    trackEvent('Feature-7094629539429452113', 'User views templates');

    this.state.choosingTemplate = true;
    // Unlock stylus, because if locked, actionResetStylus will infinitely loop
    // on actionChooseTemplate
    this.actionUnlockStylus();
    this.actionDisableToolKeys();
    this.actionResetStylus();
  }

  actionAbortChooseTemplate() {
    this.state.choosingTemplate = false;
    this.actionEnableToolKeys();
  }

  actionBeginObjectTransformation() {
    this.state.hasActiveObjectTransformation = true;
  }

  actionEndObjectTransformation() {
    this.state.hasActiveObjectTransformation = false;
  }

  actionSetPanning() {
    this.state.isPanning = true;
  }

  actionClearPanning() {
    this.state.isPanning = false;
    // Only reset the stylus if not locked via toolbar
    if (!this.state.spaceBarPan && !this.isStylusLocked(this.stylusPan)) {
      this.canvas.controller.actionResetPanStylus();
    }
  }

  actionSetPanStylus() {
    // Ignore subsequent calls if pan stylus is already set
    if (this.state.currentStylus.isCurrentStylus(this.stylusPan)) {
      return;
    }
    this._stylusBeforePan = this._currentStylus;
    this.actionSetStylus(this.stylusPan);
  }

  actionResetPanStylus() {
    if (this._stylusBeforePan) {
      this.actionSetStylus(this._stylusBeforePan);
    } else {
      this.actionResetStylus();
    }
  }

  async actionAddExternalShape(shapeName: ShapeName) {
    // Used to handle external shapes, e.g. images, records
    switch (shapeName) {
      case 'image':
        return this.actionAddImage();

      case 'record':
        return await this.actionAddAhaRecord();
    }
  }

  // Prompt the user for an image
  actionAddImage() {
    this.imageInput?.click();
  }

  async actionAddAhaRecord() {
    if (this.props.selectAhaRecords) {
      const records = await this.props.selectAhaRecords();

      const recordUrls = records.map(
        record => `${location.origin}${record.path}`
      );

      this.actionAddAhaRecords(recordUrls);
    }
  }

  actionAddAhaRecords(recordUrls: string[]) {
    const parsedRecordData = parseUrl(this, recordUrls.join('\n'));

    if (parsedRecordData) {
      this.actionPlaceObjects(parsedRecordData, {
        select: true,
        shouldZoom: false,
      });
    }
  }

  actionPlaceAhaRecord(recordUrl: string, left: number, top: number) {
    const parsedRecordData = parseUrl(this, recordUrl);

    if (!parsedRecordData) {
      console.error('Failed to parse record URL', recordUrl);
      return;
    }

    // Position the records at the specified location
    return this.actionPlaceObjectsAtPoint(
      parsedRecordData,
      { x: left, y: top },
      {
        shouldZoom: false,
      }
    );
  }

  actionSidePanelStateChange(state: SidePanelExternalState) {
    const { visible } = state;

    Object.assign(this.state, { sidePanelOpen: visible });
  }

  annotationIdExists(id: string): boolean {
    return this.state.annotationIdCache.has(id);
  }

  actionAddAnnotationId(id: string): void {
    this.state.annotationIdCache.add(id);
  }

  getCommentPosition(commentableObj: fabric.Object) {
    const commentSize = Comment.commentSize();
    let width = commentableObj.getScaledWidth();
    let height = commentableObj.getScaledHeight();
    let left = commentableObj.left;
    let top = commentableObj.top;

    // If the commentable object is a table, we want to position the comment
    // relative to the selected cells (if there are any).
    if (isTable(commentableObj) && commentableObj.hasSelectedCell()) {
      const selectedCells = commentableObj._getSelectedCells();
      if (selectedCells.length) {
        left = Math.min(
          ...selectedCells.map(c => (c ? c.left : Number.MAX_SAFE_INTEGER))
        );
        top = Math.min(
          ...selectedCells.map(c => (c ? c.top : Number.MAX_SAFE_INTEGER))
        );
        width = Math.max(
          ...selectedCells.map(c => (c ? c.width + c.left - left : 0))
        );
        height = Math.max(
          ...selectedCells.map(c => (c ? c.height + c.top - top : 0))
        );
      }
    }

    // Position comment in top right corner.
    // If object width is less than comment, center x.
    // If object height is less than comment, center y.
    return {
      x:
        width > commentSize
          ? left + width - commentSize - 5
          : left + width / 2 - commentSize / 2,
      y: height > commentSize ? top + 5 : top + height / 2 - commentSize / 2,
    };
  }

  actionCreateComment({
    pointer,
    commentableObj,
  }: {
    pointer?: { x: number; y: number };
    commentableObj?: fabric.Object;
  }): void {
    this.state.draftingComment = true;
    this.actionOpenSidePanel('comments');
    let position = this.mousePosition;
    if (commentableObj) {
      position = this.getCommentPosition(commentableObj);
    } else if (pointer) {
      position = {
        x: pointer.x - Comment.commentSize() / 2,
        y: pointer.y - Comment.commentSize() / 2,
      };
    }
    this.externalSetCommentsVisible?.(true);

    const commentObj = new Comment({
      controller: this,
      authorId: this.state.userId,
      ownerUserId: this.state.userId,
      left: position.x,
      top: position.y,
      commentable: commentableObj?.id ?? null,
      annotationsManagerId: this.annotationsManager.annotationsManagerId,
    });

    this.addToCanvas(commentObj);
    this.annotationsManager.newAnnotationUndoEntry =
      this.undoManager.currentUndoEntry();

    this.canvas.setActiveObject(commentObj);
    this.canvas.requestRenderAll();

    this.undoManager.completeUndoGroup();

    this.annotationsManager.newAnnotation(commentObj);
  }

  addToCanvas<T extends fabric.Object & CollaborationBehaviors>(obj: T): T {
    const where = isFrameLike(obj) ? 'bottom' : 'top';
    this.canvas.addObjects([obj], { where });
    obj.created(this);
    obj.fire('created');
    if (hasFrameMemberBehaviors(obj)) {
      obj.maybeSetParentFrame();
    }
    return obj;
  }

  actionCommentComplete(): void {
    this.state.draftingComment = false;
  }

  // If comment visibility is toggled in the side panel, update every comment
  // object on the canvas to reflect the new visibility state.
  actionUpdateCommentVisibility(): void {
    if (this.canvas) {
      this.canvas
        .getObjects()
        .filter(obj => isComment(obj) || isSimpleGroup(obj))
        .forEach(obj => {
          if (isComment(obj)) {
            obj.updateVisibility();
          }
          if (isSimpleGroup(obj)) {
            obj.forEachObjectDeep(o => {
              if (isComment(o)) {
                o.updateVisibility();
              }
            });
          }
        });
      this.removeHiddenCommentsFromSelection();
      this.canvas.requestRenderAll();
    }
  }

  // If comment visibility is toggled or a comment is marked resolved and the
  // side panel does not show resolved comments, we need to remove any hidden
  // comments from the current selection (if there is one)
  removeHiddenCommentsFromSelection(): void {
    const activeObject = this.canvas.getActiveObject();
    if (activeObject) {
      if (isSimpleSelection(activeObject)) {
        const objects: fabric.Object[] = [];
        for (const obj of activeObject.getObjects()) {
          if (isComment(obj) && obj.visible === false) {
            activeObject.removeWithUpdate(obj);
          } else {
            objects.push(obj);
          }
        }
        if (objects.length === 1) {
          this.canvas.setActiveObject(objects[0]);
        }
      } else if (isComment(activeObject) && activeObject.visible === false) {
        this.canvas.discardActiveObject();
      }
    }
  }

  actionShowResolvedComments(): void {
    this.state.showResolvedComments = true;
    this.actionUpdateCommentVisibility();
  }

  actionHideResolvedComments(): void {
    this.state.showResolvedComments = false;
    this.actionUpdateCommentVisibility();
  }

  actionOpenSidePanel(
    activeItemId?: string,
    options?: Record<string, any>
  ): void {
    if (this.sidePanelController) {
      this.sidePanelController.actionOpenSidePanel(activeItemId, options);
    }
  }

  actionCloseSidePanel() {
    if (this.sidePanelController) {
      this.sidePanelController.actionCloseSidePanel();
    }
  }

  async actionCreateImageShape(
    imageSrc: DataURL,
    placeAtPoint: IPoint,
    file: File,
    options = {}
  ) {
    const imageShape = await this.createImageShape(
      imageSrc,
      placeAtPoint,
      file,
      options
    );
    this.canvas.setActiveObject(imageShape).requestRenderAll();
    return imageShape;
  }

  private async createImageShape(
    imageSrc: DataURL,
    placeAtPoint: IPoint,
    file: File,
    options = {}
  ) {
    const shapeName = ALLOWED_VIDEO_TYPES.includes(file.type)
      ? 'video'
      : 'image';
    // Create the image on the canvas.
    const imageShape = Shape.create<ShapeImage | ShapeVideo>({
      controller: this.proxiedThis,
      shapeName,

      left: placeAtPoint.x,
      top: placeAtPoint.y,
      angle: 0,
      opacity: 1,
      scaleX: 1,
      scaleY: 1,
      height: 0, // Will be set when image loads.
      width: 0,
      fill: null,
      contentType: file.type,

      ...options,
    });

    const undoEntry = this.undoManager.currentUndoEntry();
    imageShape.shape.loadImage(imageSrc);
    if (isShapeImage(imageShape)) {
      imageShape.shape.setLocalPlaceholderImageFromBlob(file);
    }

    // Upload the image to the server.
    try {
      this.state.activeAttachmentUploads.push(imageShape.id);
      const attachmentId = await this.uploadAttachment!({ file });
      if (!attachmentId) throw new Error('Unable to upload image');
      this.undoManager.addUndo(undoEntry, () => {
        imageShape.set({ attachmentId } as Partial<typeof imageShape>);
      });
    } catch (e) {
      console.error(e);
      this.actionShowMessage({
        message: `Unable to upload image. Error message was '${e.message}'.`,
        type: 'error',
      });

      // Remove the UndoEntry too, so the user can't undo the broken image
      // back into existence.
      this.undoManager.completeUndoGroup(); // Don't include any unrelated changes.
      this.undoManager.removeUndo(undoEntry); // Undo for create.
      imageShape.deleted(this.proxiedThis);
      this.canvas.remove(imageShape);
      const deleteEntry = this.undoManager.completeUndoGroup()!;
      this.undoManager.removeUndo(deleteEntry); // Undo for delete.
    } finally {
      this.state.activeAttachmentUploads =
        this.state.activeAttachmentUploads.filter(id => id !== imageShape.id);
    }

    return imageShape;
  }

  async actionUploadImage(file: File, placeAtPoint?: IPoint) {
    if (
      !ALLOWED_IMAGE_TYPES.includes(file.type) &&
      !ALLOWED_VIDEO_TYPES.includes(file.type)
    ) {
      this.actionShowMessage(
        `Supported image and video types are JPEG, PNG, GIF, WEBP, MP4, OGG and WEBM. You uploaded ${file.type}.`
      );
      return;
    }

    // Read the image as a data URL.
    const reader = new FileReader();
    reader.onload = async event => {
      const imageSrc = event.target?.result;
      if (typeof imageSrc === 'string' && imageSrc.startsWith('data:')) {
        await this.createAndPositionImage(
          imageSrc as DataURL,
          file,
          placeAtPoint
        );
      } else {
        console.error('Expected string from readAsDataURL:', typeof imageSrc);
      }
    };
    reader.readAsDataURL(file);
  }

  private async createAndPositionImage(
    imageSrc: DataURL,
    file: File,
    placeAtPoint?: IPoint
  ) {
    const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
    let centerPointAutomatically = false;

    if (!placeAtPoint) {
      if (isImage) {
        // If we were not given a location, size the image to the screen
        // and then center it.
        //
        // Start by placing the image off screen, hopefully where it does not
        // overlap with any frames, then size and reposition it once we know its
        // actual size.
        placeAtPoint = { x: 10000, y: 10000 };
        centerPointAutomatically = true;
      } else {
        placeAtPoint = this.canvas.getVpCenter();
      }
    }

    const imageShape = await this.createImageShape(
      imageSrc,
      placeAtPoint,
      file
    );

    if (isImage) {
      // Shrink the image to fit the screen if necessary
      const vptCoords = this.canvas.vptCoords;

      if (vptCoords) {
        // Zooming out makes the viewport larger. Scale vptWidth and vptHeight
        // by the zoom so we can pretend we're at 100% zoom when calculating
        // how to scale the image to fit.
        const zoom = this.canvas.getZoom();
        const vptWidth = (vptCoords.tr.x - vptCoords.tl.x) * zoom;
        const vptHeight = (vptCoords.bl.y - vptCoords.tl.y) * zoom;
        // Based on the aspect ratio of the image and the viewport, scale the
        // image to fit the viewport
        if (imageShape.width > vptWidth || imageShape.height > vptHeight) {
          const imageAspect = imageShape.width / imageShape.height;
          const vptAspect = vptWidth / vptHeight;

          if (imageAspect > vptAspect) {
            // Image is wider than the viewport, so scale to fit the width.
            imageShape.scaleToWidth(0.8 * vptWidth);
          } else {
            // Image is taller than the viewport, so scale to fit the height.
            // Allow for enough room so the toolbar is above or below the
            // image.
            imageShape.scaleToHeight(0.8 * vptHeight);
          }
        }
      }

      if (centerPointAutomatically) {
        imageShape.viewportCenter();
      }
    }

    this.canvas.setActiveObject(imageShape).requestRenderAll();
  }

  async actionPlaceTemplate(globalTemplate) {
    trackEvent('Feature-7094629539429452113', 'User applies a template', {
      'Template name': globalTemplate.name,
    });

    const templateDoc = await globalTemplate.whiteboard.editorContent;

    // Empty personal/workspace templates will have `null` content
    if (templateDoc) {
      void this.actionPlaceContent(templateDoc);
    }
  }

  async actionPlaceContent(content) {
    // Create a copy of the template with remapped IDs.
    const newDoc = await Serialization.cloneDocumentWithAttachments(
      content,
      this
    );

    this.actionPlaceObjects(newDoc);
  }

  async actionPlaceEmbeddedBookmark(bookmarkId: RecordId) {
    const bookmark = {
      type: 'embeddedBookmark',
      width: EMBEDDED_CONTENT_WIDTH,
      height: EMBEDDED_CONTENT_HEIGHT,
      bookmarkId,
      ...this.defaultsManager.for('embeddedBookmark'),
    };

    const doc = await Serialization.cloneDocumentWithAttachments(
      { version, objects: [bookmark] },
      this
    );

    this.actionPlaceObjects(doc);
  }

  async actionPlaceAssistantArtifact({
    token,
    version,
  }: {
    token: string;
    version?: string;
  }) {
    const artifact = {
      type: 'embeddedAssistantArtifact',
      width: EMBEDDED_CONTENT_WIDTH,
      height: EMBEDDED_CONTENT_HEIGHT,
      artifactToken: token,
      ...(version ? { artifactVersion: version } : {}),
      ...this.defaultsManager.for('embeddedAssistantArtifact'),
    };

    const doc = await Serialization.cloneDocumentWithAttachments(
      { version, objects: [artifact] },
      this
    );

    this.actionPlaceObjects(doc);
  }

  async actionPlaceSampleTemplate(template) {
    trackEvent('Feature-7094629539429452113', 'User applies a template', {
      'Template name': template.name,
    });

    const templateDoc = await template.loadTemplateDocument();
    void this.actionPlaceContent(templateDoc);
  }

  actionPlaceObjects(
    whiteboard: DocData | fabric.Object[],
    {
      select,
      placement = 'right',
      shouldZoom = true,
      ...zoomOptions
    }: PlaceObjectsOptions = {}
  ): fabric.Object | fabric.Object[] {
    let point: IPoint | undefined;
    if (this.canvas.size() > 0) {
      const canvasExtent = this.canvas.usedPortion();
      point = {
        x: canvasExtent.left,
        y: canvasExtent.top,
      };

      switch (placement) {
        case 'bottom':
          point.y += canvasExtent.height + 100;
          break;
        case 'right':
        default:
          point.x += canvasExtent.width + 100;
          break;
      }
    }

    const objects = this.actionPlaceObjectsAtPoint(whiteboard, point, {
      shouldZoom,
      ...zoomOptions,
    });

    if (select) {
      if (objects.length === 1) {
        this.canvas.setActiveObject(objects[0]);
        return objects[0];
      } else if (objects.length > 1) {
        const selection = new SimpleSelection(objects, { canvas: this.canvas });
        this.canvas.setActiveObject(selection);
      }
    }

    return objects.length === 1 ? objects[0] : objects;
  }

  actionPlaceObjectsAtPoint(
    whiteboard: DocData | fabric.Object[],
    point?: IPoint,
    zoomOptions: ZoomToOptions & { shouldZoom?: boolean } = {
      shouldZoom: true,
    }
  ): fabric.Object[] {
    let objects: fabric.Object[] = [];

    if (Array.isArray(whiteboard)) {
      objects = whiteboard;
    } else {
      // Restore the document back onto the canvas.
      // makeActiveSelectionFromDocData does exactly this, but with it filters the objects using
      // getFilteredObjectsForSelection. This messes with the moveToCoords call below. Reverting to
      // the old logic until we can figure that out
      objects = Serialization.restoreDocument(this, whiteboard);
    }
    const selectedObject =
      objects.length === 1
        ? objects[0]
        : new SimpleSelection(objects, {
            canvas: this.canvas,
          });

    if (!point) {
      const bounds = this.canvas.calcViewportBoundaries();

      const width = bounds.tr.x - bounds.tl.x;
      const height = bounds.bl.y - bounds.tl.y;

      const selectionWidth = selectedObject.width * selectedObject.scaleX;
      const selectionHeight = selectedObject.height * selectedObject.scaleY;

      point = {
        x: bounds.tl.x + width / 2 - selectionWidth / 2,
        y: bounds.tl.y + height / 2 - selectionHeight / 2,
      };
    }

    // Place the new objects to the right of the existing objects.
    selectedObject.moveToCoords({ left: point.x, top: point.y });
    selectedObject.bringToFront();

    if (isSimpleSelection(selectedObject)) {
      // Clean up SimpleSelection, otherwise this can get left on the canvas without
      // a way to clean it up easily
      selectedObject.destroy();
    }

    this.undoManager.completeUndoGroup();

    const { shouldZoom, ...zoom } = zoomOptions;
    if (shouldZoom) {
      // Zoom the selection into view.
      zoomAndPanTo(this, [selectedObject], zoom);
    }

    return objects;
  }

  // Whiteboards can be shared outside of an account. From outside of
  // an account, you cannot create records or link to records inside
  // of the account. When this happens, we should not be able to
  // resolve record info, and should turn off the linking
  // functionality.
  allowRecordLinking(): this is RecordInterface {
    const { resolveRecordInfo, openAhaDrawer, openRecordInDrawer } = this.props;
    return !!(resolveRecordInfo && openAhaDrawer && openRecordInDrawer);
  }

  // Since a whiteboard may contain hundreds of records we need to make the API
  // calls more efficiently than one by one. This code collects all of the
  // record lookups that happen in a single event cycle, makes a single API
  // call (per record type) to the server for all of those records, then passes
  // out the results to each caller.
  //
  // We only look up 100 records in a single call since GraphQL is paginated
  // anyway. So rather than paginating the GraphQL response we split into
  // groups here.
  async fetchRecordInfo(
    recordType: WhiteboardRecordTypeNames | WhiteboardRecordModels['typename'],
    recordId: RecordId
  ): Promise<WhiteboardRecordInfo> {
    if (!this.allowRecordLinking()) {
      return await Promise.resolve({
        typename: 'NotFound',
      });
    }

    const recordTypename = normalizeRecordType(recordType);
    let cardView: Aha.CardView | undefined;

    if (recordTypeHasCardLayout(recordTypename)) {
      // Wait for the card layout manager to be ready before we load the records
      await this.cardLayoutManager.ready;
      cardView = this.cardLayoutManager.getCardView(recordTypename);
    }

    return await this.props.resolveRecordInfo!(recordType, recordId, {
      fetchRecordLinks: true,
      cardView,
    });
  }

  setAnimationCancelFunction(func) {
    this.animationCancelFunction = func;
  }

  actionShowCursorName(cursorId) {
    this.cursorManager.showCursorName(cursorId);
  }

  // Not an action because it doesn't update state.
  trackCursor(event: React.MouseEvent | MouseEvent) {
    if (!this.canvas) return;
    // Don't send viewers cursor positions
    if (!this.props.allowUpdate && !this.props.allowAnnotate) return;

    // Use currentTarget so we still get the correct position when mouse is
    // over a contextmenu or toolbar.

    const rect = (
      event.currentTarget as HTMLCanvasElement
    ).getBoundingClientRect();
    const canvasPoint = this.canvas.pointToCanvasCoords({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    });
    this.cursorManager.sendCursor(canvasPoint);
  }

  private stylusCache: Partial<Record<string, StylusShape>> = {};

  getStylus({
    objectType,
    shapeName,
    options,
  }: DistributeUnion<StylusDefinition>): StylusShape {
    const key = `${objectType}-${shapeName}`;

    if (!this.stylusCache[key]) {
      this.stylusCache[key] = new StylusShape({
        controller: this,
        shapeName,
      });
    }

    const stylus = this.stylusCache[key];
    stylus.options = options;
    return stylus;
  }

  private createStyluses() {
    this.stylusPointer = new StylusPointer({ controller: this.proxiedThis });
    this.stylusPan = new StylusPan({ controller: this.proxiedThis });

    this.defaultShapeStylus = new StylusShape({
      controller: this.proxiedThis,
      shapeName: 'rect',
    });
    const stylusCurrentShape = new StylusCurrentShape({
      controller: this.proxiedThis,
      currentStylus: this.defaultShapeStylus,
    });
    this.currentShapeStylus = stylusCurrentShape;

    this.currentConnectorStylus = new StylusConnector({
      controller: this.proxiedThis,
      options: {
        lineRouting: ConnectorRouting.ROUTING_STRAIGHT,
        endStyles: [CONNECTOR_END_STYLE.NONE, CONNECTOR_END_STYLE.FILLED_ARROW],
      },
    });
    this.stylusText = new StylusShape({
      controller: this.proxiedThis,
      shapeName: 'text',
    });
    this.stylusImage = new StylusShape({
      controller: this.proxiedThis,
      shapeName: 'image',
    });
    this.stylusNote = new StylusNote({
      controller: this.proxiedThis,
    });
    this.stylusStamp = new StylusSvgStamp({ controller: this.proxiedThis });
    this.stylusPen = new StylusPen({ controller: this.proxiedThis });
    this.stylusHighlighter = new StylusHighlighter({
      controller: this.proxiedThis,
    });
    this.defaultPenStylus = this.stylusPen;
    this.currentPenStylus = new StylusCurrentPen({
      controller: this.proxiedThis,
      currentStylus: this.defaultPenStylus,
    });
    this.defaultFrameStylus = new StylusFrame({ controller: this.proxiedThis });
    const stylusCurrentFrame = new StylusCurrentFrame({
      controller: this.proxiedThis,
      currentStylus: this.defaultFrameStylus,
    });
    this.currentFrameStylus = stylusCurrentFrame;
    this.StylusFrame = this.defaultFrameStylus;
    this.StylusFrameAutolayout = new StylusFrameAutolayout({
      controller: this.proxiedThis,
    });
    this.StylusTemplate = new StylusTemplate({ controller: this.proxiedThis });
    this.stylusAssistant = new StylusAssistant({
      controller: this.proxiedThis,
    });
    this.stylusAssistantPromo = new StylusAssistantPromo({
      controller: this.proxiedThis,
    });

    this.stylusWireframePromo = new StylusWireframePromo({
      controller: this.proxiedThis,
    });
    this.stylusTable = new StylusTable({ controller: this.proxiedThis });
    this.stylusComment = new StylusComment({ controller: this.proxiedThis });
    this.stylusIcon = new StylusShape({
      controller: this.proxiedThis,
      shapeName: 'icon',
    });
    this.stylusSpriteIcon = new StylusShape({
      controller: this.proxiedThis,
      shapeName: 'spriteIcon',
    });
    this.stylusWireframe = new StylusWireframe({
      controller: this.proxiedThis,
      wireframeName: 'button',
    });

    // Shapes that can be transformed into other shapes.
    this.switchableShapes = [
      'roundRect',
      'rect',
      'circle',
      'triangle',
      'diamond',
      'octagon',
      'pentagon',
      'parallelogram',
      'trapezoid',
      'processEnd',
      'openBracket',
      'closedBracket',
      'callOutBox',
      'certificate',
      'star',
      'cylinder',
      'cloud',
      'arrow',
      'circleArrow',
      'manualInput',
      'offpageConnector',
      'preparation',
      'subroutine',
      'document',
    ];

    this.connectorShapeMenuShapes = [
      'note',
      'text',
      'roundRect',
      'rect',
      'circle',
      'diamond',
      'processEnd',
      'cylinder',
      'triangle',
    ];

    const smartRecordTypes = Object.keys(ShapeSmartRecord.recordTypes);
    this.smartRecordStyluses = smartRecordTypes.map(
      (recordType: WhiteboardRecordTypeNames) => {
        const stylus = new StylusShape({
          controller: this.proxiedThis,
          shapeName: 'smartRecord',
          initialOptions: { recordType },
        });
        return stylus;
      }
    );
    // Add the Aha! record card stylus to this list as well
    const recordStylus = new StylusShape({
      controller: this.proxiedThis,
      shapeName: 'record',
    });

    this.smartRecordStyluses.unshift(recordStylus);

    const components: ComponentType[] = ['stack', 'swimlane', 'board'];
    // Objects to be included in the shapes menu
    this.objects = ['table', ...components, 'smartObject'];

    this.shapeStyluses = BASIC_SHAPES.map(
      shapeName => new StylusShape({ controller: this.proxiedThis, shapeName })
    );

    this.connectorShapeMenuStyluses = this.connectorShapeMenuShapes.map(
      shapeName => new StylusShape({ controller: this.proxiedThis, shapeName })
    );

    this.componentStyluses = components.map(
      componentName =>
        new StylusComponent({
          controller: this.proxiedThis,
          componentName,
        })
    );

    this.advancedStyluses = ([] as (ObjectType | ShapeName)[])
      .concat(ADVANCED_SHAPES, this.objects)
      .map(shapeName => {
        switch (shapeName) {
          case 'table':
            return new StylusTable({ controller: this.proxiedThis });
          case 'stack':
          case 'swimlane':
          case 'board':
            return this.componentStyluses.find(
              stylus => stylus.componentName === shapeName
            );

          case 'smartObject':
            if (!smartObjectsEnabled()) {
              return undefined;
            }
            return new StylusSmartObject({ controller: this.proxiedThis });

          default:
            return new StylusShape({
              controller: this.proxiedThis,
              shapeName,
            });
        }
      })
      .filter(Boolean);

    if (this.props.renderAddAhaViewModal) {
      this.advancedStyluses.unshift(
        new StylusAddAhaView({ controller: this.proxiedThis })
      );
    }

    if (window.featureFlags?.AW_328_whiteboard_mindmaps) {
      this.advancedStyluses.unshift(
        new StylusMindMap({
          sides: ['east', 'west'],
          theme: 'branches',
          controller: this.proxiedThis,
        })
      );
    }

    this.customShapesStyluses = [];

    this.noteStyluses = noteColorPalette.map(
      color => new StylusNote({ controller: this.proxiedThis, fill: color })
    );
    this.noteStackStylus = new StylusNoteStack({
      controller: this.proxiedThis,
    });
    this.penStyluses = [this.stylusPen, this.stylusHighlighter];

    this.connectorStyluses = [
      new StylusConnector({
        controller: this.proxiedThis,
        options: {
          lineRouting: ConnectorRouting.ROUTING_STRAIGHT,
          endStyles: [CONNECTOR_END_STYLE.NONE, CONNECTOR_END_STYLE.NONE],
        },
      }),
      new StylusConnector({
        controller: this.proxiedThis,
        options: {
          lineRouting: ConnectorRouting.ROUTING_STRAIGHT,
          endStyles: [
            CONNECTOR_END_STYLE.NONE,
            CONNECTOR_END_STYLE.FILLED_ARROW,
          ],
        },
      }),
      new StylusConnector({
        controller: this.proxiedThis,
        options: {
          lineRouting: ConnectorRouting.ROUTING_STRAIGHT,
          endStyles: [
            CONNECTOR_END_STYLE.FILLED_ARROW,
            CONNECTOR_END_STYLE.FILLED_ARROW,
          ],
        },
      }),
      new StylusConnector({
        controller: this.proxiedThis,
        options: {
          lineRouting: ConnectorRouting.ROUTING_ORTHOGONAL_AUTO,
          endStyles: [
            CONNECTOR_END_STYLE.NONE,
            CONNECTOR_END_STYLE.FILLED_ARROW,
          ],
        },
      }),
      new StylusConnector({
        controller: this.proxiedThis,
        options: {
          lineRouting: ConnectorRouting.ROUTING_BEZIER,
          endStyles: [
            CONNECTOR_END_STYLE.NONE,
            CONNECTOR_END_STYLE.FILLED_ARROW,
          ],
        },
      }),
    ];
    this.stampStyluses = (
      [
        [
          'aha-icon--thumbs-up',
          'aha-icon--thumbs-down',
          'aha-icon--star',
          'aha-icon--question',
        ],
        [
          'aha-icon--slight-smile',
          'aha-icon--frowning',
          'aha-icon--smile',
          'aha-icon--disappointed',
          'aha-icon--heart-eyes',
          'aha-icon--confused',
          'aha-icon--star-struck',
          'aha-icon--astonished',
        ],
        [
          'aha-icon--slight-smile-elle',
          'aha-icon--frowning-elle',
          'aha-icon--smile-elle',
          'aha-icon--disappointed-elle',
          'aha-icon--heart-eyes-elle',
          'aha-icon--confused-elle',
          'aha-icon--star-struck-elle',
          'aha-icon--astonished-elle',
        ],
      ] as StylusSvgStampName[][]
    ).map(svgGroup =>
      svgGroup.map(
        svgName =>
          new StylusSvgStamp({
            controller: this.proxiedThis,
            options: {
              svg: svgName,
            },
          })
      )
    );

    this.currentStampStylus = new StylusSvgStamp({
      controller: this.proxiedThis,
      options: { svg: 'aha-icon--thumbs-up' },
    });

    this.allWireframes = [
      'button',
      'navMenuVertical',
      'navMenuHorizontal',
      'multiSelect',
      'link',
      'inputField',
      'dropdown',
      'datePicker',
      'searchBar',
      'checkbox',
      'radioButton',
      'toggle',
      'slider',
      'placeholder',
      'image',
      'donutChart',
      'video',
      'map',
      'pieChart',
      'lineChart',
      'barChart',
      'pill',
      'avatar',
      'scrollBar',
      'pagination',
      'breadcrumb',
      'progressStepper',
    ];

    this.devices = ['phone', 'tablet', 'laptop', 'browser'];

    this.wireframeStyluses = this.allWireframes.map(
      wireframeName =>
        new StylusWireframe({
          controller: this.proxiedThis,
          wireframeName,
        })
    );

    this.blockStyluses = getBlockStyluses(this.proxiedThis);

    this.deviceStyluses = this.devices.map(
      deviceName =>
        new StylusDevice({
          controller: this.proxiedThis,
          deviceName,
        })
    );

    this.frameRatioStyluses = [
      new StylusFrameCustom({ controller: this.proxiedThis }),
      new StylusSmartZone({ controller: this.proxiedThis }),
      new StylusFrameWide({ controller: this.proxiedThis }),
      new StylusFrameSquare({ controller: this.proxiedThis }),
    ];

    this.paletteStyluses = [
      { stylus: this.stylusPointer, tool: tools.ToolStylusPointer },
      { stylus: this.stylusText, tool: tools.ToolStylusText },
      { stylus: this.currentShapeStylus, tool: tools.ToolStylusShape },
      { stylus: this.stylusNote, tool: tools.ToolStylusNote },
      { stylus: this.currentConnectorStylus, tool: tools.ToolStylusConnector },
      { stylus: this.currentPenStylus, tool: tools.ToolStylusPen },
      { stylus: this.currentFrameStylus, tool: tools.ToolStylusFrame },
      { stylus: this.currentStampStylus, tool: tools.ToolStylusStamp },
      { stylus: this.stylusImage, tool: tools.ToolStylusImage },
      { stylus: this.StylusTemplate, tool: tools.ToolStylusTemplate },
    ];

    if (
      window.featureFlags?.awt_390_whiteboard_ai_assistant ||
      window.currentAccount?.whiteboardsAdvanced
    ) {
      // Old AI assistant only for whiteboardsAdvanced customers
      // New AI assistant available to all
      this.paletteStyluses.push({
        stylus: this.stylusAssistant,
        tool: tools.ToolStylusAssistant,
      });
    } else if (window.currentAccount?.hasCrossProductPromotionsEnabled) {
      this.paletteStyluses.push({
        stylus: this.stylusAssistantPromo,
        tool: tools.ToolStylusAssistantPromo,
      });
    }

    if (
      !window.currentAccount?.whiteboardsAdvanced &&
      window.currentAccount?.hasCrossProductPromotionsEnabled
    ) {
      this.paletteStyluses.push({
        stylus: this.stylusWireframePromo,
        tool: tools.ToolStylusWireframePromo,
      });
    }
  }

  getWhiteboardUrl() {
    if (this.props.whiteboardUrl) {
      return this.props.whiteboardUrl;
    }

    const url = new URL(window.location.origin);
    url.pathname = `/whiteboards/${this.props.whiteboardId}`;
    return url.toString();
  }

  getObjectsUrl(objects: { id: string }[]) {
    const whiteboardUrl = this.getWhiteboardUrl();

    if (objects.length === 0 || !whiteboardUrl) {
      return;
    }

    const url = new URL(whiteboardUrl);
    url.hash = '#object-' + objects.map(o => o.id).join('-');

    return url.toString();
  }

  /** determines if this is a link to a shape within this whiteboard, returning details if so */
  parseInternalLink(url: string):
    | {
        whiteboardId: string;
        hash?: string;
      }
    | undefined {
    const [path, hash] = url.split('#');

    const idFromLink = extractWhiteboardId(path);

    // is it a numeric id that matches this whiteboard's id?
    if (idFromLink === this.props.whiteboardId) {
      return { whiteboardId: idFromLink, hash };
    }

    // is it an id like NN-123 that matches this whiteboard's url?
    if (this.props.whiteboardUrl) {
      if (idFromLink === extractWhiteboardId(this.props.whiteboardUrl)) {
        return { whiteboardId: idFromLink, hash };
      }
    }
  }

  getVptKey() {
    return `vptHash-${window.location.pathname}-${this.props.whiteboardId}`;
  }

  shouldPersistVpt() {
    return (
      !this.props.isEmbeddedPreview &&
      (this.props.mode === 'inline' || this.props.mode === 'fullscreen')
    );
  }

  actionFocusAnnotation(annotationId: RecordId) {
    this.annotationsManager.focusCommentOnCanvas(annotationId, false, true);
  }

  /**
   * Zoom the canvas to either a set of object-ids or the viewport given by the
   * hash in the url. If there is no hash or it is invalid, then attempt to
   * return to the previous viewport. Return true if navigation was successful.
   */
  actionNavigate(
    hash?: string,
    animate = true,
    disableVptPersistence = false
  ): boolean {
    const vptPersistence = this.shouldPersistVpt() && !disableVptPersistence;

    const vpt = getTargetViewport(
      this,
      hash ?? decodeURIComponent(window.location.hash.slice(1)),
      vptPersistence
        ? () => localStorage.getItem(this.getVptKey()) ?? undefined
        : undefined
    );

    if (vpt) {
      if (animate) {
        animateViewportTransform(this, vpt);
      } else {
        this.canvas.setViewportTransform(vpt);
        this.canvas.fire('zoom');

        // Check if any objects are on screen after the viewport change if not return false
        // and it will zoom to fit the screen
        const anyObjectsOnScreen = this.canvas
          ?.getObjects()
          .some(obj => obj.isOnScreen());

        if (!anyObjectsOnScreen) {
          return false;
        }
      }
      return true;
    } else {
      return false;
    }
  }

  /**
   * Update the page location hash with the current viewport transform.
   */
  actionUpdateViewportHash = debounce(() => {
    const vpt = this.canvas.viewportTransform;
    if (vpt && this.shouldPersistVpt()) {
      const hash = createViewportHash(vpt);
      localStorage.setItem(this.getVptKey(), hash);
    }
  }, 300);

  actionDisableToolKeys() {
    // Only change the hotkey scope if we are the focused whiteboard
    if (document.activeElement !== this.whiteboardContainer) return;

    hotkeys.setScope('');
  }

  actionEnableSelectionKeys() {
    if (document.activeElement !== this.whiteboardContainer) return;

    hotkeys.setScope('selectionTools');
  }

  actionEnableToolKeys() {
    if (document.activeElement !== this.whiteboardContainer) return;

    hotkeys.setScope(this.hotkeysToolScope);
    hotkeys.filter = event => {
      if (event.key === 'Enter') {
        const activeObject = this.canvas?.getActiveObject();
        // If the active object is a type that needs to handle Enter key internally,
        // let the key event pass through to the object
        if (this.objectNeedsEnterKey(activeObject)) {
          return false;
        }
      }

      // Only allow other hotkeys if current target is not an editable element
      const target = (event.target || event.srcElement) as HTMLElement | null;
      const tagName = target?.tagName.toLowerCase();
      return !(
        target?.isContentEditable ||
        tagName === 'input' ||
        tagName === 'select' ||
        tagName === 'textarea'
      );
    };
  }

  objectNeedsEnterKey(object: fabric.Object | null): boolean {
    if (!object) return false;
    // Tables, frames, and mind map leaves need the enter key
    if (isTable(object) || isFrame(object) || isMindMapLeaf(object))
      return true;
    return false;
  }

  actionToggleDebug(debugSetting) {
    this.state.debug[debugSetting] = !this.state.debug[debugSetting];
    this.canvas.requestRenderAll();
  }

  actionHandleError(error) {
    console.error(error);

    // Cleanup so that no additional actions can be taken on the canvas
    this.canvas.interactive = false;
    this.deregisterToolKeyBindings();

    // Setting the error on the state causes the error boundary
    // ErrorHandler to show up on top of the canvas.
    if (!this.state.error) {
      this.state.error = Sentry.captureException(error);
    }
  }

  readOnlyTools = [
    tools.ToolZoomIn,
    tools.ToolZoomOut,
    tools.ToolPan,
    tools.ToolFitToScreen,
    tools.ToolFitSelectionToScreen,
    tools.ToolPresent,
    tools.ToolExitPresentation,
    tools.ToolNextSlide,
    tools.ToolPreviousSlide,
    tools.ToolPresentationFrames,
    tools.ToolSearch,
    tools.ToolCopy,
    tools.ToolExportPNG,
    tools.ToolExportSVG,
    tools.ToolFrameNext,
    tools.ToolFramePrevious,
    tools.ToolFrameOpenList,
  ];

  annotateOnlyTools = [
    ...this.readOnlyTools,
    tools.ToolCut,
    tools.ToolPaste,
    tools.ToolDelete,
    tools.ToolUndo,
    tools.ToolRedo,
    tools.ToolDuplicate,
  ];

  cropTools = [
    ...this.readOnlyTools,
    tools.ToolMoveUp,
    tools.ToolMoveDown,
    tools.ToolMoveLeft,
    tools.ToolMoveRight,
    tools.ToolBigMoveUp,
    tools.ToolBigMoveDown,
    tools.ToolBigMoveLeft,
    tools.ToolBigMoveRight,
    tools.ToolAbortCrop,
    tools.ToolCommitCrop,
  ];

  isToolEnabled({ tool }) {
    const { allowUpdate, allowAnnotate } = this.props;
    const selection = this.toolbarObject();

    if (this.isCropping() && !this.cropTools.includes(tool)) {
      return false;
    }

    const notEditable = (!allowUpdate && !allowAnnotate) || this.isReadOnly();

    // Whiteboard is not editable at all, only allow read only tools
    if (notEditable && !this.readOnlyTools.includes(tool)) {
      return false;
    }

    // User is a reviewer, only allow annotate tools
    if (
      !allowUpdate &&
      allowAnnotate &&
      !this.annotateOnlyTools.includes(tool)
    ) {
      return false;
    }

    return tool.enabled({ controller: this.proxiedThis, selection });
  }

  isStylusLocked(stylus) {
    const { currentStylus, stylusLocked } = this.state;
    return stylus.isCurrentStylus(currentStylus) && stylusLocked;
  }

  // Prevent selection while pan tool is selected
  allowSelection() {
    return !this.state.currentStylus.isCurrentStylus(this.stylusPan);
  }

  registerToolKeyBindings() {
    Object.values(tools).forEach(tool => {
      const shortcut = tool.shortcut.forCurrentPlatform;
      if (shortcut && shortcut.length > 0) {
        hotkeys(
          shortcut,
          { scope: this.hotkeysToolScope, element: this.whiteboardContainer },
          event => {
            // Prevent selection of stylus while panning.
            if (this.state.isPanning || (event.repeat && !tool.allowsRepeat)) {
              return false;
            }
            // Prevent stylus menu from popping up when switching back to
            // previously active stylus menu via hotkey
            this.actionSetStylusMenu(null);
            this.actionApplyTool(tool);
            // Make sure we respect the new stylus's alwaysLockWhenSelected field
            this.actionUpdateStylusLock();

            return false;
          }
        );
      }
    });

    [
      tools.ToolUndo,
      tools.ToolRedo,
      tools.ToolDeselect,
      tools.ToolCopyStyle,
      tools.ToolPasteStyle,
    ].forEach(tool => {
      const shortcut = tool.shortcut.forCurrentPlatform;
      if (shortcut && shortcut.length > 0) {
        hotkeys(
          shortcut,
          { scope: 'selectionTools', element: this.whiteboardContainer },
          () => {
            this.actionApplyTool(tool);
            return false;
          }
        );
      }
    });
  }

  private registerArrowKeyPanBindings(): void {
    const inputElements = /^(INPUT|TEXTAREA|SELECT)\$/;

    const arrowKeys = [
      { keys: 'up', dx: 0, dy: PAN_INCREMENT },
      { keys: 'down', dx: 0, dy: -PAN_INCREMENT },
      { keys: 'left', dx: PAN_INCREMENT, dy: 0 },
      { keys: 'right', dx: -PAN_INCREMENT, dy: 0 },
      { keys: 'left+right', dx: -PAN_INCREMENT, dy: 0 },
      { keys: 'right+left', dx: PAN_INCREMENT, dy: 0 },
      { keys: 'up+down', dx: 0, dy: -PAN_INCREMENT },
      { keys: 'down+up', dx: 0, dy: PAN_INCREMENT },
      { keys: 'left+down,down+left', dx: PAN_INCREMENT, dy: -PAN_INCREMENT },
      { keys: 'left+up,up+left', dx: PAN_INCREMENT, dy: PAN_INCREMENT },
      { keys: 'right+down,down+right', dx: -PAN_INCREMENT, dy: -PAN_INCREMENT },
      { keys: 'right+up,up+right', dx: -PAN_INCREMENT, dy: PAN_INCREMENT },
    ] as const;

    // Register single-axis arrow keys
    arrowKeys.forEach(({ keys, dx, dy }) => {
      hotkeys(
        keys,
        {
          scope: this.hotkeysToolScope,
          element: this.whiteboardContainer,
        },
        event => {
          if (
            !this.allowPanAndZoom() ||
            inputElements.test(document.activeElement?.tagName || '') ||
            Boolean(this.selection)
          ) {
            return true;
          }

          event.preventDefault();
          this.actionPanByIncrement(dx, dy);
          return false;
        }
      );
    });
  }

  registerKeyBindings() {
    // Tool shortcuts
    this.registerToolKeyBindings();

    // Hold space to pan
    hotkeys.unbind('Space', this.hotkeysToolScope); // Remove the ToolPan binding.
    hotkeys(
      'Space',
      {
        scope: this.hotkeysToolScope,
        keyup: true,
        keydown: true,
        element: this.whiteboardContainer,
      },
      event => {
        // If pan tool selected, ignore space bar. Ending pan wont matter,
        // as we do not set a previous stylus in ToolPan
        if (event.repeat || this.isStylusLocked(this.stylusPan)) {
          return false;
        }
        if (event.type === 'keydown' && !this.state.isPanning) {
          this.actionSetPanStylus();
          this.state.spaceBarPan = true;
        }
        if (event.type === 'keyup') {
          this.state.spaceBarPan = false;
          if (!this.state.isPanning) {
            this.actionResetPanStylus();
          }
        }

        return false;
      }
    );

    this.registerArrowKeyPanBindings();
    // Tab and Shift+Tab to cycle through objects
    // Only capture Tab when there's already a selection to avoid overriding
    // default browser behavior
    hotkeys(
      'tab,shift+tab',
      {
        scope: this.hotkeysToolScope,
        element: this.whiteboardContainer,
      },
      event => {
        // Only intercept Tab if there's an active selection
        if (!this.selection) {
          return true;
        }

        // Let MindMapLeaf handle Tab to create child nodes
        const activeObject = this.canvas.getActiveObject();
        if (isMindMapLeaf(activeObject)) {
          return true;
        }

        const forward = !event.shiftKey;
        this.actionCycleSelection(forward);

        // Prevent default behavior (moving focus to next focusable element)
        event.preventDefault();
        return false;
      }
    );
  }

  deregisterToolKeyBindings() {
    for (const tool of Object.values(tools)) {
      const shortcut = tool.shortcut.forCurrentPlatform;
      if (shortcut) {
        shortcut.length > 0 && hotkeys.unbind(shortcut);
      }
    }
  }

  get hasActiveUploads() {
    return !!this.state.activeAttachmentUploads.length;
  }

  private actionCycleSelection(forward = true) {
    const currentSelection = this.selection;
    if (!currentSelection) return;

    this.selectionCycleManager.cycleSelection(forward, currentSelection);
  }

  actionEndCropping(abort = false) {
    if (!abort) {
      switch (this.state.mode) {
        case MODE.CROP:
          this.imageToCrop?.shape?.commitCropping(this.crop!.values);
          break;
      }
      // Complete undo here, as all possible commit crop routes go through here
      this.undoManager.completeUndoGroup();
    }

    this.state.mode = MODE.DRAW;
    this.crop?.destroy();
    this.crop = null;
    this.imageToCrop = null;
    this.canvas.off('mouse:down', this.maybeEndCropping);
    this.canvas.requestRenderAll();
  }

  maybeEndCropping(eventData: fabric.IEvent) {
    const { target } = eventData;
    if (!this.state.isPanning && !this.crop?.wasClicked(target)) {
      this.actionEndCropping();
    }
  }

  actionStartCropping(image: Shape<ShapeImage>) {
    this.state.mode = MODE.CROP;
    this.imageToCrop = image;
    this.canvas.on('mouse:down', this.maybeEndCropping);
    this.crop = new Crop(image);
    this.renderManager.refreshObject(image);
  }

  isCropping() {
    return this.state.mode === MODE.CROP;
  }

  whiteboardContentAsJSON() {
    const objects = this.canvas._objects;
    return Serialization.collectionToJSON(objects, this.whiteboardId!);
  }

  get contextMenuClickPoint() {
    if (!this.state.contextMenu) return null;
    return { x: this.state.contextMenu.x, y: this.state.contextMenu.y };
  }

  get contextMenuCanvasPoint() {
    if (!this.state.contextMenu) return null;
    const { canvasX, canvasY } = this.state.contextMenu;
    if (canvasX !== undefined && canvasY !== undefined) {
      return { x: canvasX, y: canvasY };
    }
    return null;
  }

  get hasUndo(): boolean {
    return this.state.hasUndo;
  }

  get hasRedo(): boolean {
    return this.state.hasRedo;
  }

  get unlockAllAvailable(): boolean {
    return tools.ToolUnlockAll.available({
      controller: this,
    });
  }

  actionAddDependencyLines() {
    this.actionApplyTool(tools.ToolAddDependencyLines);
  }

  actionRemoveDependencyLines() {
    this.actionApplyTool(tools.ToolRemoveDependencyLines);
  }

  actionRefreshDependencyLines() {
    this.actionApplyTool(tools.ToolRefreshDependencyLines);
  }

  actionUnlockAllItems() {
    this.actionApplyTool(tools.ToolUnlockAll);
  }

  actionToggleShowShapeAuthors(showShapeAuthors: boolean) {
    this.renderManager.refreshAll();
    this.state.showShapeAuthors = showShapeAuthors;
    this.canvas?.requestRenderAll();
  }

  actionSetPointerInputType(type: 'auto' | 'mouse' | 'trackpad') {
    this.state.pointerInputType = type;
    if (this.hasLocalStorageSupport) {
      localStorage.setItem('_whiteboard_pointer_input_type', type);
    }
  }

  actionToggleBackgroundGrid(showBackgroundGrid: boolean) {
    this.state.showBackgroundGrid = showBackgroundGrid;
    this.canvas?.requestRenderAll();
    trackEvent(
      'Feature-7267336276979895864',
      showBackgroundGrid
        ? 'User shows whiteboard grid'
        : 'User hides whiteboard grid'
    );
  }

  // This is a debug-only action for displaying a grid in the background of the
  // whiteboard, useful to debug positioning and dimensions of elements.
  actionToggleDebugGrid() {
    this.state.debug.enableGrid = !this.state.debug.enableGrid;
    this.canvas?.requestRenderAll();
  }

  // This is a debug-only action for displaying a scale element at 0,0 in the
  // whiteboard.
  actionToggleDebugScale() {
    this.state.debug.enableScale = !this.state.debug.enableScale;
    this.canvas?.requestRenderAll();
  }

  // This is a debug-only action for displaying debug guides on Mind Maps
  actionToggleDebugMindMapBranches() {
    this.state.debug.mindmap.showBranches =
      !this.state.debug.mindmap.showBranches;
    this.actionUpdateMindMapDebug();
  }

  actionToggleDebugMindMapSelectable() {
    this.state.debug.mindmap.selectable = !this.state.debug.mindmap.selectable;
    this.actionUpdateMindMapDebug();
  }

  actionToggleDebugMindMapSlowAnimations() {
    this.state.debug.mindmap.slowAnimations =
      !this.state.debug.mindmap.slowAnimations;
    this.actionUpdateMindMapDebug();
  }

  actionUpdateMindMapDebug() {
    for (const obj of this.canvas.getObjects()) {
      if (isMindMap(obj)) {
        obj.updateDebug();
      }
    }
  }

  actionUpdateUndoRedoState(hasUndo: boolean, hasRedo: boolean) {
    this.state.hasUndo = hasUndo;
    this.state.hasRedo = hasRedo;
  }

  actionShowDrawerPath(path) {
    this.setDrawerPath(path);
  }

  actionCloseDrawer() {
    this.setDrawerPath('');
  }

  setDrawerPath(path) {
    this.state.drawerPath = path;
  }

  // This action can be called from aha-app so we can export a whiteboard
  // using controls outside of the Whiteboard component.
  actionExportWhiteboard(type: 'png' | 'svg'): void {
    const tool = type === 'png' ? tools.ToolExportPNG : tools.ToolExportSVG;
    this.actionApplyTool(tool);
  }

  async actionDuplicateAttachments(
    attachmentMapping: { [objId: RecordId]: RecordId },
    oldWhiteboardId: RecordId
  ): Promise<{ [objId: RecordId]: RecordId }> {
    if (!this.duplicateAttachments) return attachmentMapping;

    try {
      return await this.duplicateAttachments(
        attachmentMapping,
        oldWhiteboardId
      );
    } catch (error) {
      console.error(error);
      return attachmentMapping;
    }
  }

  async actionGetFileNameFromRecord() {
    // If we don't have access to models or this whiteboard is shared,
    // return and revert to default title in exportFileName() (Untitled)
    if (
      !window.aha?.models ||
      !this.props.canRequestFilename ||
      !this.allowRecordLinking() ||
      !this.whiteboardId
    ) {
      return;
    }

    try {
      const { Whiteboard } = window.aha.models;
      const whiteboard = await Whiteboard.select(
        'id',
        'documentExportTitle'
      ).find(this.whiteboardId);
      this.state.exportFileName = whiteboard.documentExportTitle;
    } catch (e) {
      console.error('Could not request file name from record', e);
    }
  }

  get exportFileName() {
    return this.state.exportFileName || 'Untitled';
  }

  actionLinkHovered(
    hoveredLink: HoveredLink | undefined,
    tooltipPosition?: 'top'
  ) {
    const hoverController = this.findControllerInstance(HoverController);
    if (hoverController) {
      hoverController.actionLinkHovered(hoveredLink, tooltipPosition);
    }
  }

  addEventListener(
    type: string,
    listener: (event: CustomEvent<Record<string, unknown>>) => void,
    options?: boolean | AddEventListenerOptions
  ) {
    this.eventTarget.addEventListener(type, listener, options);
  }

  removeEventListener(
    type: string,
    listener: (event: CustomEvent<Record<string, unknown>>) => void,
    options?: boolean | EventListenerOptions
  ) {
    this.eventTarget.removeEventListener(type, listener, options);
  }

  /**
   * Dispatch a custom event through the controller's event system.
   *
   * Event names should be past tense (e.g. "clicked", "updated") and should describe
   * something that has happened, not than something we want to happen.
   * It should not matter to the dispatcher whether the event is handled or not.
   * Include a unique id in detail if possible.
   * Subscribe to events using addEventListener or the useSubscription hook.
   *
   * @param eventName - The name of the event to dispatch
   * @param detail - Optional data to include with the event
   */
  dispatchEvent(eventName: string, detail: Record<string, unknown> = {}) {
    this.eventTarget.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  // Detect if any parent controller uses the RoutingBehaviors mixin.
  clientSideRouting() {
    return !!this.findController(controller => 'router' in controller);
  }

  get isShared() {
    return !!this.props.sharingOptions?.getSharingToken;
  }

  async actionOpenWireframePromo() {
    trackEvent('Feature-7333230858416702915', 'User clicks promotion', {
      'Upgrade location': 'Whiteboards stylus toolbar wireframes',
      'Promotion product': 'Whiteboards',
    });

    const promoLink =
      '/suite/whiteboards?upgrade_location=whiteboards_stylus_toolbar_wireframe';
    window.open(promoLink, '_blank');
  }

  async actionOpenAssistantPromo() {
    const promoLink = `/suite/whiteboards?upgrade_location=whiteboards_ai_assistant`;
    window.open(promoLink, '_blank');
  }

  actionShowSearchBar() {
    this.state.searchBarVisible = true;
  }

  actionHideSearchBar() {
    this.state.searchBarVisible = false;
    this.actionFocus();
  }

  searchBarVisible() {
    return !!this.state.searchBarVisible;
  }

  actionShowUrlInputModal(fabricObject: fabric.Object) {
    if (!hasLinkToUrlBehaviors(fabricObject)) {
      return;
    }

    this.canvas.discardActiveObject();
    this.canvas.requestRenderAll();

    this.state.urlInputModalObject = fabricObject;

    this.actionFocus();
  }

  setOpenRecordLinkModalFunc(openRecordLinkModalFunc) {
    this.openRecordLinkModal = openRecordLinkModalFunc;
  }

  actionSetRecordLinkItems(recordLinkItems: RecordLinkItem[]) {
    this.openRecordLinkModal(recordLinkItems);
  }

  actionSetSmartRecordItems() {
    this.actionOpenSidePanel('ahaShapes');
  }

  actionToggleAIAssistant(newState?: boolean) {
    this.state.aiAssistantVisible = newState ?? !this.state.aiAssistantVisible;
  }

  private functionsDeferredDuringBulkAdd?: Array<() => void>;

  // When you add, modify or delete objects, there can be many side effects that
  // fire. If you are doing many of these operations at once, you might want
  // those side effects to happen after all the operations are done.
  //
  // For example, pasting a frame with children, we want to wait until
  // all three have been added before the layout calculations fire.
  //
  // Using this function in conjunction with performBulkSensitiveCode will
  // defer the code inside performBulkSensitiveCode until after the
  // surroundedSection is finished.
  //
  // Usage:
  //   aroundBulkAddingCode(() => {
  //     this.canvas.add(newObject);
  //     this.canvas.add(newObject2);
  //   });
  //
  //   Somewhere else that fires as a result of the addition
  //   performBulkSensitiveCode(() => {
  //     this.canvas.layout();
  //   });
  aroundBulkAddingCode(surroundedSection) {
    if (this.functionsDeferredDuringBulkAdd) {
      // If we want to be able to nest this call, we have to keep track of how
      // many times it's been entered, and then process after the last exit.
      throw new Error('aroundBulkAddingCode cannot be nested.');
    }

    this.functionsDeferredDuringBulkAdd = [];

    try {
      // Execute the function that might trigger layout calls
      surroundedSection();

      let count = 0;
      while (this.functionsDeferredDuringBulkAdd.length > 0) {
        const func = this.functionsDeferredDuringBulkAdd.shift();
        if (func) func();

        if (++count > 1000) {
          console.error(
            'Too many deferred functions in bulk add',
            this.functionsDeferredDuringBulkAdd
          );
          break;
        }
      }
    } finally {
      this.functionsDeferredDuringBulkAdd = undefined;
    }
  }

  // Used in conjunction with aroundBulkAddingCode. If we are still inside a
  // call to aroundBulkAddingCode, then the function passed to this will be
  // deferred until after aroundBulkAddingCode is done. Otherwise, the passed
  // function will execute immediately.
  performBulkSensitiveCode(sensitiveFunction) {
    if (this.functionsDeferredDuringBulkAdd) {
      this.functionsDeferredDuringBulkAdd.push(sensitiveFunction);
    } else {
      sensitiveFunction();
    }
  }

  async loadSpriteSheets() {
    const sheets = await loadCloudArchIcons();
    await SpriteSvg.getInstance().loadSpriteSheets(sheets);
    this.loadSpritesInCanvas();
  }

  // Let existing sprite shapes in the canvas know that their svgs are available
  loadSpritesInCanvas() {
    this.canvas.forEachObject(object => {
      if (isShapeSpriteIcon(object)) {
        object.shape.prepareImage();
      }
    });
  }

  cancelViewportAnimation() {
    if (this.animationCancelFunction) {
      this.animationCancelFunction();
      this.animationCancelFunction = null;
    }
  }

  moreMenuActionsState() {
    return {
      addAvailable: tools.ToolAddDependencyLines.available({
        controller: this,
      }),
      removeAvailable: tools.ToolRemoveDependencyLines.available({
        controller: this,
      }),
    };
  }

  // Store controller on a DOM node so others can have access to it.
  storeControllerInDOM(canvasContainer: React.RefObject<HTMLDivElement>) {
    let whiteboardControllerElement = canvasContainer?.current?.closest(
      '[data-whiteboard-controller-store-element]'
    ) as HTMLElement | null;

    if (whiteboardControllerElement) {
      // We have a case where the more menu is stored oustide the whiteboard
      // container. We could try and prevent the whiteboard container from
      // having the data attribute so there is only one, but it's easier to just
      // traverse to the heighest one at this point. We could store the
      // controller on both elements, but that is not necessary yet.
      const higherElement = whiteboardControllerElement.parentElement?.closest(
        '[data-whiteboard-controller-store-element]'
      ) as HTMLElement | null;

      if (higherElement) {
        whiteboardControllerElement = higherElement;
      }

      whiteboardControllerElement.whiteboardController = this;
    }
  }

  smartZonesEnabled() {
    return !!this.props.smartZoneFormData;
  }

  /**
   * Tests if the current viewport is empty of objects
   */
  isViewportEmpty(): boolean {
    return this.canvas.isViewportEmpty();
  }

  getContentSummary() {
    return getWhiteboardContent(this.canvas.getObjects());
  }

  /** Applies create, update and delete operations to the whiteboard */
  async actionProcessOperations(
    operations: WhiteboardOperation[],
    groupId: string,
    animate = true
  ) {
    const opts = { groupId, animate };
    for (const operation of operations) {
      const valid = await this.assistant.handleStreamChunk(operation, opts);
      debugAssistant('Operation processed', { operation, valid });
    }

    if (!opts.animate) {
      void zoomAndPanTo(this, this.canvas.usedPortion(), {
        animate: false,
      });
    }

    // TODO only do this if the operations actually changed something
    this.undoManager.completeUndoGroup();
  }

  actionAddGeneratedWireframe(wireframeXML: string) {
    this.assistant.convertXMLToWhiteboard(wireframeXML);
  }

  async actionAddDiagram(diagram: string, opts?: { animate?: boolean }) {
    await this.assistant.addDiagram(diagram, opts);
  }

  registerCanvasSelectionChangeListener(
    callback: (objects: fabric.Object[]) => void
  ) {
    this.canvas.selectionChangeListeners.push(callback);
  }

  getWhiteboardSelectionForAssistant() {
    return getWhiteboardContent(this.canvas.getActiveObjects());
  }

  getCurrentColorPalette() {
    return getCurrentColorPalette();
  }

  actionShowPointerInputHelp() {
    this.state.showPointerInputHelpModal = true;
  }

  actionHidePointerInputHelp() {
    this.state.showPointerInputHelpModal = false;
  }

  actionCreateSequentialShape(fabricObject: fabric.Object) {
    return createSequentialShape(fabricObject, this);
  }
}

export default WhiteboardController;
